import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  FlaskConical,
  Info,
  Loader2,
  PauseCircle,
  Play,
  RefreshCw,
  RotateCcw,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { arenaApi } from "./data/api";
import {
  benchmarkKeytermAttachmentView,
  keytermPreparationItemView,
} from "./data/keyterm-preparation";
import {
  judgeOnlyProviderView,
  judgeOnlySliceCounts,
} from "./data/judge-only";
import type {
  BenchmarkOptions,
  BenchmarkJudgeEvaluationContext,
  BenchmarkPreview,
  BenchmarkProviderSummary,
  BenchmarkReview,
  BenchmarkReferenceReviewItem,
  BenchmarkRow,
  BenchmarkRowsPage,
  BenchmarkRun,
  BenchmarkRunConfig,
  KeytermSource,
  JudgeCalibration,
  JudgeCalibrationItem,
  KeytermPreparation,
  BenchmarkKeytermPairsPage,
} from "./data/types";
import { AudioPlayer } from "./AudioPlayer";
import {
  CALIBRATION_REQUIRED_POLICIES,
  DEFAULT_JUDGE_POLICY_VERSION,
  JUDGE_POLICY_OPTIONS,
} from "./data/judge-contract";
import {
  benchmarkHintVariantCellCounts,
  benchmarkMaterialErrorCount,
  mergeBenchmarkSummaries,
} from "./data/summary";
import {
  cellReviewKeytermModeOptions,
  cellReviewRunFingerprint,
  cellReviewTrackLabel,
  cellReviewVariantLabel,
  isCellReviewBaseline,
  resolveCellReviewKeytermMode,
} from "./data/cell-review-filters";

const PAGE_SIZE = 40;
// Why the arena withheld a weighted rate. "No scored cell" means the pool was
// empty; "no audited cell" means it held cells that nobody reviewed, so every
// value in it came from a provider error or an empty transcript.
const WEIGHTED_RATE_UNAVAILABLE_LABELS: Record<string, string> = {
  no_scored_observations: "withheld: no scored cell",
  insufficient_audit_coverage: "withheld: no audited cell",
};
const CELL_REVIEW_POLL_INTERVAL_MS = 10_000;
const ACTIVE = new Set(["queued", "running", "cancel_requested"]);

const pct = (value: number | null | undefined, digits = 1) =>
  value == null ? "—" : `${(value * 100).toFixed(digits)}%`;
const ms = (value: number | null | undefined) =>
  value == null ? "—" : `${Math.round(value)} ms`;
const pp = (value: number | null | undefined) =>
  value == null ? "—" : `${value > 0 ? "+" : ""}${(value * 100).toFixed(1)} pp`;
const signedMs = (value: number | null | undefined) =>
  value == null ? "—" : `${value > 0 ? "+" : ""}${Math.round(value)} ms`;
const dateTime = (value: string) => new Date(value).toLocaleString();
const formatError = (cause: unknown) =>
  cause instanceof Error ? cause.message : String(cause);
const formatSplit = (value: string) => value === "locked_holdout" ? "Evaluation (locked holdout)" : value.replaceAll("_", " ");
// ponytail: short form for the 230px run sidebar, where formatSplit's long label would ellipsize away.
const shortSplit = (value: string) => value === "locked_holdout" ? "evaluation" : value.replaceAll("_", " ");
const percentile = (values: number[], fraction: number) => {
  if (!values.length) return null;
  const ordered = [...values].sort((left, right) => left - right);
  return ordered[Math.max(0, Math.ceil(ordered.length * fraction) - 1)];
};
const REVIEW_ERROR_TYPES = [
  "number_change",
  "sign_or_negation",
  "unit_or_operator_change",
  "entity_or_keyterm_change",
  "word_substitution",
  "omission",
  "insertion_or_hallucination",
  "fragmentation",
  "other",
];
const GENERATED_KEYTERM_SOURCES = new Set<KeytermSource>([
  "reference_derived",
  "slide_context",
]);

const keytermSourceLabel = (source: KeytermSource) => {
  if (source === "slide_context") return "Published slide data";
  if (source === "reference_derived") return "Reference-derived";
  return "Reviewed terms";
};
function MetricLabel({ label, help }: { label: string; help: string }) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  return (
    <span className="stt-metric-label">
      {label}
      <button
        className="stt-metric-help"
        type="button"
        title={help}
        aria-label={`${label}: ${help}`}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen((current) => !current)}
        onFocus={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <Info size={12} strokeWidth={2} />
      </button>
      {open ? <span id={tooltipId} className="stt-metric-tooltip" role="tooltip">{help}</span> : null}
    </span>
  );
}

function RunViewEmptyState({
  view,
  run,
}: {
  view: "Overview" | "Performance";
  run: BenchmarkRun;
}) {
  const pending = ACTIVE.has(run.status);
  return (
    <section className="stt-run-view-empty" role="status">
      <Info size={17} />
      <div>
        <strong>{pending ? `${view} analytics pending` : `${view} analytics unavailable`}</strong>
        <p>
          {pending
            ? `Run is ${run.status}; analytics will appear after benchmark cells finish.`
            : "This run has no stored analytics summary. Rebuild analytics to regenerate it without rerunning providers."}
        </p>
      </div>
    </section>
  );
}

type ScoreTrack = "human_adjusted" | "judge_only";
type BenchmarkView = "overview" | "judge" | "cells" | "performance" | "calibration";

const judgeGateLabel = (reason: string) => {
  const [provider, code] = reason.split(":");
  const labels: Record<string, string> = {
    insufficient_luna_coverage: "needs more Luna cells",
    luna_availability_below_threshold: "Luna availability below 99.5%",
    proof_rejection_present: "proof evidence rejected",
    proof_verification_below_threshold: "proof verification below 99.5%",
    proof_verification_spread_above_threshold: "provider proof-health spread too wide",
    repeated_agreement_below_threshold: "repeated decision agreement below 98%",
    three_way_disagreement_present: "three-way judge disagreement",
    luna_availability_spread_above_threshold: "provider availability spread too wide",
  };
  return provider && code && labels[code]
    ? `${provider}: ${labels[code]}`
    : labels[reason] ?? reason.replaceAll("_", " ");
};

function ScoreTrackControl({
  value,
  onChange,
}: {
  value: ScoreTrack;
  onChange: (value: ScoreTrack) => void;
}) {
  return (
    <div className="stt-segment stt-segment--ghost" role="group" aria-label="Score track">
      <button
        type="button"
        className={value === "human_adjusted" ? "active" : ""}
        aria-pressed={value === "human_adjusted"}
        onClick={() => onChange("human_adjusted")}
      >
        Human audit diagnostic
      </button>
      <button
        type="button"
        className={value === "judge_only" ? "active" : ""}
        aria-pressed={value === "judge_only"}
        onClick={() => onChange("judge_only")}
      >
        LLM judge
      </button>
    </div>
  );
}

function PercentWithCi({
  value,
  ci,
}: {
  value: number | null;
  ci: [number, number] | null;
}) {
  return (
    <>
      {pct(value)}
      {ci ? <small className="stt-ci">{pct(ci[0])} to {pct(ci[1])}</small> : null}
    </>
  );
}

function MillisecondsWithCi({
  value,
  ci,
}: {
  value: number | null;
  ci: [number, number] | null;
}) {
  return (
    <>
      {ms(value)}
      {ci ? <small className="stt-ci">{ms(ci[0])} to {ms(ci[1])}</small> : null}
    </>
  );
}

function GainMetric({
  label,
  value,
  ci,
  milliseconds = false,
}: {
  label: string;
  value: number | null | undefined;
  ci?: [number, number] | null;
  milliseconds?: boolean;
}) {
  const format = milliseconds ? signedMs : pp;
  return (
    <span>
      <strong>{label}</strong> {format(value)}
      {ci ? <small className="stt-ci">{format(ci[0])} to {format(ci[1])}</small> : null}
    </span>
  );
}

function initialConfig(options: BenchmarkOptions): BenchmarkRunConfig {
  return {
    dataset_version: options.dataset_versions[0] ?? "",
    splits: ["development"],
    cohorts: [],
    duration_bands: [],
    provider_keys: options.providers.map((provider) => provider.key),
    keyterm_mode: "off",
    keyterm_source: "reviewed",
    judge_enabled: true,
    sample_limit: 0,
    seed: 42,
  };
}

export function BenchmarkRuns({ initialRunId }: { initialRunId?: string } = {}) {
  const [options, setOptions] = useState<BenchmarkOptions | null>(null);
  const [config, setConfig] = useState<BenchmarkRunConfig | null>(null);
  const [preview, setPreview] = useState<BenchmarkPreview | null>(null);
  const [preparation, setPreparation] = useState<KeytermPreparation | null>(null);
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  const [selected, setSelected] = useState<BenchmarkRun | null>(null);
  const [rows, setRows] = useState<BenchmarkRowsPage | null>(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [providerFilter, setProviderFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [errorTypeFilter, setErrorTypeFilter] = useState("");
  const [keytermModeFilter, setKeytermModeFilter] = useState("");
  const [keytermSourceFilter, setKeytermSourceFilter] = useState("");
  const [unclearReasonFilter, setUnclearReasonFilter] = useState("");
  const [referenceQualityFilter, setReferenceQualityFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [cellReviewRefreshing, setCellReviewRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calibrations, setCalibrations] = useState<JudgeCalibration[]>([]);
  const [judgeEvaluations, setJudgeEvaluations] = useState<BenchmarkJudgeEvaluationContext[]>([]);
  const [selectedJudgeEvaluation, setSelectedJudgeEvaluation] = useState<BenchmarkJudgeEvaluationContext | null>(null);
  const [selectedJudgeEvaluationId, setSelectedJudgeEvaluationId] = useState("");
  const [requestedJudgePolicyVersion, setRequestedJudgePolicyVersion] = useState(DEFAULT_JUDGE_POLICY_VERSION);
  const rowsRequestId = useRef(0);
  const rowsInFlight = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const rowsFilterKey = useRef<string | null>(null);
  const preparationRequestId = useRef(0);
  const regenerationIdempotencyKeys = useRef(new Map<string, string>());
  const selectedRunId = selected?.run_id;
  const selectedStatus = selected?.status;
  const selectedRunFingerprint = cellReviewRunFingerprint(
    selectedStatus,
    selected?.completed_cells,
  );
  const selectedKeytermSource = selected?.config.keyterm_source ?? "reviewed";

  const resetRowFilters = useCallback(() => {
    setProviderFilter("");
    setDecisionFilter("");
    setReviewFilter("");
    setErrorTypeFilter("");
    setKeytermModeFilter("");
    setKeytermSourceFilter("");
    setUnclearReasonFilter("");
    setReferenceQualityFilter("");
    setOffset(0);
  }, []);

  const loadRuns = useCallback(async () => {
    const response = await arenaApi.benchmarkRuns();
    setRuns(response.items);
    setSelected((current) => {
      if (!current) return response.items[0] ?? null;
      return response.items.find((run) => run.run_id === current.run_id) ?? current;
    });
  }, []);

  useEffect(() => {
    Promise.all([arenaApi.benchmarkOptions(), arenaApi.benchmarkRuns()])
      .then(([loadedOptions, loadedRuns]) => {
        setOptions(loadedOptions);
        setConfig(initialConfig(loadedOptions));
        setRuns(loadedRuns.items);
        setSelected(loadedRuns.items.find((run) => run.run_id === initialRunId) ?? loadedRuns.items[0] ?? null);
      })
      .catch((cause) => setError(formatError(cause)))
      .finally(() => setLoading(false));
  }, []);

  const loadCalibrations = useCallback(async () => {
    const response = await arenaApi.judgeCalibrations();
    setCalibrations(response.items);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadCalibrations().catch((cause) => setError(formatError(cause)));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadCalibrations]);

  const loadSelected = useCallback(async () => {
    if (!selectedRunId) return;
    const run = await arenaApi.benchmarkRun(selectedRunId);
    setSelected(run);
    setRuns((current) =>
      current.map((item) => (item.run_id === run.run_id ? run : item)),
    );
  }, [selectedRunId]);

  const loadJudgeEvaluations = useCallback(async () => {
    if (!selectedRunId) {
      setJudgeEvaluations([]);
      setSelectedJudgeEvaluation(null);
      setSelectedJudgeEvaluationId("");
      return;
    }
    const response = await arenaApi.benchmarkJudgeEvaluations(selectedRunId);
    setJudgeEvaluations(response.items);
    const nextId = response.items.find((item) => item.active)?.evaluation_id
      ?? response.items[0]?.evaluation_id
      ?? "";
    setSelectedJudgeEvaluationId((current) =>
      response.items.some((item) => item.evaluation_id === current)
        ? current
        : nextId,
    );
    setSelectedJudgeEvaluation((current) =>
      current && response.items.some((item) => item.evaluation_id === current.evaluation_id)
        ? current
        : null,
    );
  }, [selectedRunId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadJudgeEvaluations().catch((cause) => setError(formatError(cause)));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadJudgeEvaluations]);

  const loadSelectedJudgeEvaluation = useCallback(async () => {
    if (!selectedRunId || !selectedJudgeEvaluationId) {
      setSelectedJudgeEvaluation(null);
      return;
    }
    const evaluation = await arenaApi.benchmarkJudgeEvaluation(
      selectedRunId,
      selectedJudgeEvaluationId,
    );
    setSelectedJudgeEvaluation(evaluation);
  }, [selectedRunId, selectedJudgeEvaluationId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadSelectedJudgeEvaluation().catch((cause) => setError(formatError(cause)));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadSelectedJudgeEvaluation]);

  const loadRows = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    const filterKey = [
      selectedRunId,
      selectedKeytermSource,
      selectedJudgeEvaluationId,
      providerFilter,
      decisionFilter,
      reviewFilter,
      errorTypeFilter,
      keytermModeFilter,
      keytermSourceFilter,
      unclearReasonFilter,
      referenceQualityFilter,
      offset,
    ].join("\u0001");
    const requestKey = `${filterKey}\u0001${selectedRunFingerprint}`;
    if (rowsInFlight.current?.key === requestKey) {
      return rowsInFlight.current.promise;
    }
    // Same filters, new cell population: a progress tick on the run already on
    // screen. Refresh underneath the current rows instead of blanking them.
    const quiet = background || rowsFilterKey.current === filterKey;
    rowsFilterKey.current = filterKey;

    const requestId = ++rowsRequestId.current;
    const request = (async () => {
      if (!selectedRunId) {
        setRows(null);
        setRowsLoading(false);
        return;
      }
      if (!quiet) setRowsLoading(true);
      try {
        const page = await arenaApi.benchmarkRows(selectedRunId, {
          providerKey: providerFilter || undefined,
          decision: decisionFilter || undefined,
          reviewStatus: reviewFilter || undefined,
          errorType: errorTypeFilter || undefined,
          keytermMode: resolveCellReviewKeytermMode(
            selectedKeytermSource,
            keytermModeFilter,
          ),
          keytermSource: keytermSourceFilter || undefined,
          unclearReason: unclearReasonFilter || undefined,
          referenceQuality: referenceQualityFilter || undefined,
          judgeEvaluationId: selectedJudgeEvaluationId || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (requestId === rowsRequestId.current) setRows(page);
      } finally {
        if (requestId === rowsRequestId.current && !quiet) setRowsLoading(false);
      }
    })();
    rowsInFlight.current = { key: requestKey, promise: request };
    try {
      await request;
    } finally {
      if (rowsInFlight.current?.promise === request) rowsInFlight.current = null;
    }
  }, [selectedRunId, selectedRunFingerprint, selectedKeytermSource, selectedJudgeEvaluationId, providerFilter, decisionFilter, reviewFilter, errorTypeFilter, keytermModeFilter, keytermSourceFilter, unclearReasonFilter, referenceQualityFilter, offset]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadRows().catch((cause) => setError(formatError(cause)));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadRows]);

  useEffect(() => {
    const runActive = Boolean(selectedStatus && ACTIVE.has(selectedStatus));
    const evaluationActive = Boolean(
      selectedJudgeEvaluationId
      && selectedJudgeEvaluation?.evaluation_id === selectedJudgeEvaluationId
      && ["queued", "running"].includes(selectedJudgeEvaluation.status),
    );
    if (!selectedRunId || (!runActive && !evaluationActive)) return;
    const timer = window.setInterval(() => {
      const refreshes: Promise<unknown>[] = [];
      if (runActive) refreshes.push(loadSelected());
      if (evaluationActive) {
        refreshes.push(loadJudgeEvaluations(), loadSelectedJudgeEvaluation());
      }
      Promise.all(refreshes)
        .catch((cause) => setError(formatError(cause)));
    }, CELL_REVIEW_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [
    selectedRunId,
    selectedStatus,
    selectedJudgeEvaluationId,
    selectedJudgeEvaluation?.evaluation_id,
    selectedJudgeEvaluation?.status,
    loadJudgeEvaluations,
    loadSelectedJudgeEvaluation,
    loadSelected,
  ]);

  const refreshCellReview = useCallback(async () => {
    if (!selectedRunId) return;
    setCellReviewRefreshing(true);
    try {
      await loadRows({ background: true });
    } catch (cause) {
      setError(formatError(cause));
      throw cause;
    } finally {
      setCellReviewRefreshing(false);
    }
  }, [selectedRunId, loadRows]);

  const updateConfig = (next: BenchmarkRunConfig) => {
    preparationRequestId.current += 1;
    setConfig(next);
    setPreview(null);
    setPreparation(null);
  };

  const previewRun = async () => {
    if (!config) return;
    setWorking(true);
    setError(null);
    const requestId = ++preparationRequestId.current;
    try {
      if (GENERATED_KEYTERM_SOURCES.has(config.keyterm_source)) {
        let next = await arenaApi.createKeytermPreparation(
          config,
          crypto.randomUUID(),
        );
        if (requestId !== preparationRequestId.current) return;
        setPreparation(next);
        while (next.status === "queued" || next.status === "running") {
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
          if (requestId !== preparationRequestId.current) return;
          next = await arenaApi.keytermPreparation(next.preparation_id);
          if (requestId !== preparationRequestId.current) return;
          setPreparation(next);
        }
        if (next.status !== "completed" || !next.preview) {
          throw new Error(next.failure_reason ?? "Keyterm preparation failed.");
        }
        setPreview(next.preview);
      } else {
        setPreparation(null);
        setPreview(await arenaApi.benchmarkPreview(config));
      }
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const regeneratePreparation = async () => {
    if (!preparation?.regeneration_available) return;
    setWorking(true);
    setError(null);
    const requestId = ++preparationRequestId.current;
    const preparationId = preparation.preparation_id;
    const idempotencyKey =
      regenerationIdempotencyKeys.current.get(preparationId) ?? crypto.randomUUID();
    regenerationIdempotencyKeys.current.set(preparationId, idempotencyKey);
    try {
      let next = await arenaApi.regenerateKeytermPreparation(
        preparationId,
        idempotencyKey,
      );
      if (requestId !== preparationRequestId.current) return;
      setPreparation(next);
      while (next.status === "queued" || next.status === "running") {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        if (requestId !== preparationRequestId.current) return;
        next = await arenaApi.keytermPreparation(next.preparation_id);
        if (requestId !== preparationRequestId.current) return;
        setPreparation(next);
      }
      if (next.status !== "completed" || !next.preview) {
        throw new Error(next.failure_reason ?? "Keyterm regeneration failed.");
      }
      setPreview(next.preview);
      regenerationIdempotencyKeys.current.delete(preparationId);
      toast("Keyterms regenerated with fresh extraction inputs.");
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const startRun = async () => {
    if (!config || !preview) return;
    setWorking(true);
    setError(null);
    try {
      // Prepared keyterm runs must start with exact config used to freeze manifest.
      const frozenConfig = preview.config;
      // Manifest hash validates run inputs; idempotency key identifies this start attempt.
      const runIdempotencyKey = crypto.randomUUID();
      const run = await arenaApi.createBenchmarkRun(
        frozenConfig,
        preview.preview_hash,
        runIdempotencyKey,
        preparation?.preparation_id,
      );
      setSelected(run);
      setJudgeEvaluations([]);
      setSelectedJudgeEvaluation(null);
      setSelectedJudgeEvaluationId("");
      setPreview(null);
      setPreparation(null);
      await loadRuns();
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const runAction = async (action: "cancel" | "resume") => {
    if (!selected) return;
    setWorking(true);
    setError(null);
    try {
      const run =
        action === "cancel"
          ? await arenaApi.cancelBenchmarkRun(selected.run_id)
          : await arenaApi.resumeBenchmarkRun(selected.run_id);
      setSelected(run);
      await loadRuns();
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const rebuildSummary = async () => {
    if (!selected) return;
    setWorking(true);
    setError(null);
    try {
      const run = await arenaApi.rebuildBenchmarkSummary(selected.run_id);
      setSelected(run);
      await Promise.all([loadRuns(), loadSelectedJudgeEvaluation()]);
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const createJudgeEvaluation = async () => {
    if (!selected) return;
    setWorking(true);
    setError(null);
    try {
      const evaluation = await arenaApi.createBenchmarkJudgeEvaluation(selected.run_id, {
        policyVersion: requestedJudgePolicyVersion,
      });
      await loadJudgeEvaluations();
      setSelectedJudgeEvaluationId(evaluation.evaluation_id);
      setSelectedJudgeEvaluation(evaluation);
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  const activateJudgeEvaluation = async () => {
    if (
      !selected
      || !selectedJudgeEvaluation
      || selectedJudgeEvaluation.evaluation_id === "legacy"
      || selectedJudgeEvaluation.evaluation_id !== selectedJudgeEvaluationId
    ) return;
    setWorking(true);
    setError(null);
    try {
      const run = await arenaApi.activateBenchmarkJudgeEvaluation(
        selected.run_id,
        selectedJudgeEvaluation.evaluation_id,
      );
      setSelected(run);
      await Promise.all([
        loadRuns(),
        loadJudgeEvaluations(),
        loadSelectedJudgeEvaluation(),
        loadRows(),
      ]);
      toast.success("Judge evaluation activated");
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="stt-loading">
        <Loader2 className="stt-spin" size={18} /> Loading benchmark runs…
      </div>
    );
  }

  if (!options || !config) {
    return <div className="stt-empty stt-empty--err">{error ?? "Benchmark options unavailable."}</div>;
  }

  return (
    <div className="stt-benchmark-page">
      {error ? (
        <div className="stt-benchmark-error">
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button type="button" title="Dismiss" onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      ) : null}

      <BenchmarkConfigForm
        options={options}
        config={config}
        preview={preview}
        preparation={preparation}
        working={working}
        onChange={updateConfig}
        onPreview={previewRun}
        onRegenerate={regeneratePreparation}
        onStart={startRun}
      />

      <JudgeCalibrationPanel
        runs={runs}
        calibrations={calibrations}
        working={working}
        onChanged={() => loadCalibrations().catch((cause) => setError(formatError(cause)))}
        onError={(cause) => setError(formatError(cause))}
      />

      <div className="stt-benchmark-workspace">
        <RunHistory
          runs={runs}
          selectedId={selected?.run_id ?? null}
          onSelect={(run) => {
            rowsRequestId.current += 1;
            rowsFilterKey.current = null;
            setSelected(run);
            setRows(null);
            setRowsLoading(false);
            setJudgeEvaluations([]);
            setSelectedJudgeEvaluation(null);
            setSelectedJudgeEvaluationId("");
            resetRowFilters();
          }}
          onRefresh={() => loadRuns().catch((cause) => setError(formatError(cause)))}
        />
        <RunDetail
          run={selected && selectedJudgeEvaluation?.summary
            ? {
                ...selected,
                summary: mergeBenchmarkSummaries(
                  selectedJudgeEvaluation.active ? selected.summary : {},
                  selectedJudgeEvaluation.summary,
                ),
              }
            : selected}
          rows={rows}
          rowsLoading={rowsLoading}
          judgeEvaluations={judgeEvaluations}
          selectedJudgeEvaluation={selectedJudgeEvaluation}
          selectedJudgeEvaluationId={selectedJudgeEvaluationId}
          requestedJudgePolicyVersion={requestedJudgePolicyVersion}
          offset={offset}
          providerFilter={providerFilter}
          decisionFilter={decisionFilter}
          reviewFilter={reviewFilter}
          errorTypeFilter={errorTypeFilter}
          keytermModeFilter={keytermModeFilter}
          keytermSourceFilter={keytermSourceFilter}
          unclearReasonFilter={unclearReasonFilter}
          referenceQualityFilter={referenceQualityFilter}
          working={working}
          onProviderFilter={(value) => {
            setProviderFilter(value);
            setOffset(0);
          }}
          onDecisionFilter={(value) => {
            setDecisionFilter(value);
            setOffset(0);
          }}
          onReviewFilter={(value) => {
            setReviewFilter(value);
            if (value === "audit") {
              setProviderFilter("");
              setDecisionFilter("");
              setKeytermModeFilter("");
              setKeytermSourceFilter("");
              setUnclearReasonFilter("");
              setReferenceQualityFilter("");
            }
            setOffset(0);
          }}
          onErrorTypeFilter={(value) => {
            setErrorTypeFilter(value);
            setOffset(0);
          }}
          onErrorDrillDown={(provider, errorType) => {
            setProviderFilter(provider);
            setDecisionFilter("");
            setReviewFilter("");
            setErrorTypeFilter(errorType);
            setKeytermModeFilter("");
            setKeytermSourceFilter("");
            setUnclearReasonFilter("");
            setReferenceQualityFilter("");
            setOffset(0);
          }}
          onKeytermModeFilter={(value) => {
            setKeytermModeFilter(value);
            setOffset(0);
          }}
          onKeytermSourceFilter={(value) => {
            setKeytermSourceFilter(value);
            setOffset(0);
          }}
          onUnclearReasonFilter={(value) => {
            setUnclearReasonFilter(value);
            setOffset(0);
          }}
          onReferenceQualityFilter={(value) => {
            setReferenceQualityFilter(value);
            setOffset(0);
          }}
          onClearFilters={resetRowFilters}
          onPage={setOffset}
          onCancel={() => runAction("cancel")}
          onResume={() => runAction("resume")}
          onRebuild={rebuildSummary}
          onJudgeEvaluationChange={(evaluationId) => {
            setSelectedJudgeEvaluation(null);
            setSelectedJudgeEvaluationId(evaluationId);
            setOffset(0);
          }}
          onRequestedJudgePolicyChange={setRequestedJudgePolicyVersion}
          onCreateJudgeEvaluation={createJudgeEvaluation}
          onActivateJudgeEvaluation={activateJudgeEvaluation}
          cellReviewRefreshing={cellReviewRefreshing}
          onRefreshCells={refreshCellReview}
          onReviewed={async () => {
            await Promise.all([
              loadSelected(),
              loadSelectedJudgeEvaluation(),
              loadRows(),
            ]);
          }}
        />
      </div>
    </div>
  );
}

function BenchmarkConfigForm({
  options,
  config,
  preview,
  preparation,
  working,
  onChange,
  onPreview,
  onRegenerate,
  onStart,
}: {
  options: BenchmarkOptions;
  config: BenchmarkRunConfig;
  preview: BenchmarkPreview | null;
  preparation: KeytermPreparation | null;
  working: boolean;
  onChange: (config: BenchmarkRunConfig) => void;
  onPreview: () => void;
  onRegenerate: () => void;
  onStart: () => void;
}) {
  const counts = options.dataset_counts[config.dataset_version] ?? {};
  const keytermTrack =
    config.keyterm_mode === "off"
      ? "off"
      : config.keyterm_source;
  const sourceOptions = options.keyterm_sources ?? [
    { value: "reviewed" as const, label: "Reviewed terms", enabled: true, generated: false },
    { value: "reference_derived" as const, label: "Reference-derived", enabled: !!options.reference_keyterm_track_enabled, generated: true },
    { value: "slide_context" as const, label: "Published slide data", enabled: !!options.slide_hint_track_enabled, generated: true },
  ];
  const toggle = (field: "cohorts" | "duration_bands" | "provider_keys", value: string) => {
    const current = config[field];
    onChange({
      ...config,
      [field]: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    });
  };
  const canPreview =
    !!config.dataset_version &&
    config.splits.length > 0 &&
    (config.keyterm_source === "slide_context"
      ? config.provider_keys.length > 0
      : config.provider_keys.length >= 2);
  // The backend limits generated keyterm tracks to the same splits, so the split
  // is locked rather than merely discouraged. Gate on keytermTrack, not
  // keyterm_source, so a config that still names a generated source with the
  // track off is not locked.
  // The backend gates each frozen split separately — validation behind one flag,
  // the locked holdout behind two — and publishes hint_holdout_enabled as the
  // conjunction, so the picker follows that field rather than re-deriving it
  // from holdout_enabled and offering a split the run would fail validation on.
  const hintSplits = [
    "development",
    ...(options.hint_validation_enabled ? ["validation"] : []),
    ...(options.hint_holdout_enabled ? ["locked_holdout"] : []),
  ];
  const generatedTrackLocksSplit = GENERATED_KEYTERM_SOURCES.has(keytermTrack as KeytermSource);
  const splitLockReason = generatedTrackLocksSplit
    ? `${keytermSourceLabel(keytermTrack as KeytermSource)} keyterms run on ${hintSplits.join(" and ")} only.`
    : undefined;
  const preparationSource = preparation?.keyterm_source ?? config.keyterm_source;
  const generationContract = preparation?.generation_contract;

  return (
    <section className="stt-benchmark-config" aria-labelledby="benchmark-config-title">
      <div className="stt-benchmark-section-head">
        <div>
          <h3 id="benchmark-config-title"><FlaskConical size={17} /> New benchmark</h3>
          <p>Approved labels only · {options.fixed.chunk_ms} ms chunks · {options.fixed.cadence} cadence</p>
        </div>
        <span className="stt-badge stt-badge--ghost">Judge {options.fixed.judge_model}</span>
      </div>

      <div className="stt-benchmark-config-grid">
        <label className="stt-filter-control">
          <span className="stt-field-label">Dataset</span>
          <select
            className="stt-select"
            value={config.dataset_version}
            onChange={(event) => onChange({ ...config, dataset_version: event.target.value })}
          >
            {options.dataset_versions.map((version) => (
              <option key={version} value={version}>{version}</option>
            ))}
          </select>
        </label>

        <fieldset>
          <legend className="stt-field-label">Splits</legend>
          <div className="stt-check-row">
            {["development", "validation", ...(options.holdout_enabled ? ["locked_holdout"] : [])].map((split) => {
              const locked = generatedTrackLocksSplit && !hintSplits.includes(split);
              return (
                // The title sits on the label: a disabled input receives no
                // pointer events, so its own tooltip never opens.
                <label key={split} title={locked ? splitLockReason : undefined}>
                  <input
                    type="radio"
                    name="benchmark-split"
                    checked={config.splits.includes(split)}
                    disabled={locked}
                    onChange={() => onChange({ ...config, splits: [split] })}
                  />
                  {split.replaceAll("_", " ")} ({counts[split] ?? 0})
                </label>
              );
            })}
          </div>
          {splitLockReason ? (
            // A disabled radio takes no focus, so the reason has to be readable
            // without hovering it.
            <small role="status">{splitLockReason} Set the keyterm track to Off to benchmark the remaining splits.</small>
          ) : null}
        </fieldset>

        <fieldset>
          <legend className="stt-field-label">Providers</legend>
          <div className="stt-check-row">
            {options.providers.map((provider) => (
              <label key={provider.key}>
                <input
                  type="checkbox"
                  checked={config.provider_keys.includes(provider.key)}
                  onChange={() => toggle("provider_keys", provider.key)}
                />
                {provider.provider} · {provider.model}
                {keytermTrack === "slide_context" && provider.hint_capabilities ? (
                  <small className="stt-provider-capabilities">
                    {provider.hint_capabilities.includes("prompt") ? "prompt + keywords" : provider.hint_capabilities.includes("keywords") ? "keywords" : "baseline only"}
                  </small>
                ) : null}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="stt-filter-control">
          <span className="stt-field-label">
            <MetricLabel
              label="Keyterm source"
              help={`Choose frozen reviewed terms, reference-derived terms, or terms generated from current published slide data. Generated tracks are diagnostics limited to ${hintSplits.join(" and ")}.`}
            />
          </span>
          <select
            className="stt-select"
            value={keytermTrack}
            onChange={(event) => {
              const track = event.target.value;
              if (track === "off") {
                onChange({
                  ...config,
                  keyterm_mode: "off",
                  keyterm_source: "reviewed",
                });
              } else {
                const source = track as KeytermSource;
                const generated = GENERATED_KEYTERM_SOURCES.has(source);
                const providerKeys = config.provider_keys.length
                  ? config.provider_keys
                  : options.providers.map((provider) => provider.key);
                onChange({
                  ...config,
                  // Only rewrite the split when the current one is out of reach:
                  // a validation split the flag allows should survive picking a
                  // generated track.
                  splits:
                    generated && !hintSplits.includes(config.splits[0] ?? "")
                      ? ["development"]
                      : config.splits,
                  keyterm_mode: "both",
                  keyterm_source: source,
                  provider_keys: providerKeys,
                });
              }
            }}
          >
            <option value="off">Off</option>
            {sourceOptions.filter((source) => source.enabled).map((source) => (
              <option key={source.value} value={source.value} disabled={source.value === "reviewed" && (counts.keyterms ?? 0) === 0}>
                {source.label}{source.generated ? ` (${hintSplits.join("/")} diagnostic)` : " (paired)"}
              </option>
            ))}
          </select>
          {keytermTrack === "slide_context" ? (
            <small>Builds shared keywords from current published slide data for selected providers. Prompt and combined variants run only where provider capabilities support them. Select all providers for independent comparison.</small>
          ) : keytermTrack === "reference_derived" ? (
            <small>Uses approved reference to estimate an upper bound. Recommendation metrics remain baseline-only.</small>
          ) : (counts.keyterms ?? 0) === 0 ? (
            <small>No reviewed keyterms in this dataset</small>
          ) : null}
        </label>

        <label className="stt-filter-control">
          <span className="stt-field-label">Sample limit</span>
          <input
            className="stt-input"
            type="number"
            min={0}
            value={config.sample_limit}
            onChange={(event) => onChange({ ...config, sample_limit: Number(event.target.value) || 0 })}
          />
          <small>0 runs all matching samples</small>
        </label>

        <label className="stt-filter-control">
          <span className="stt-field-label">Seed</span>
          <input
            className="stt-input"
            type="number"
            value={config.seed}
            onChange={(event) => onChange({ ...config, seed: Number(event.target.value) || 0 })}
          />
        </label>

        <label className="stt-benchmark-toggle">
          <input
            type="checkbox"
            checked={config.judge_enabled}
            onChange={(event) => onChange({ ...config, judge_enabled: event.target.checked })}
          />
          <span>
            Semantic judge
            <small>Only normalized transcript mismatches are sent to the judge.</small>
          </span>
        </label>
      </div>

      <div className="stt-benchmark-config-actions">
        {preparation && preparation.status !== "completed" ? (
          <div className="stt-benchmark-preview">
            <strong>Preparing {keytermSourceLabel(preparationSource)} terms</strong>
            <span>{preparation.completed_samples} / {preparation.total_samples} samples</span>
            <span>{preparation.eligible_samples} eligible so far</span>
            <span>{preparation.cached_samples} cached</span>
            <span>{preparation.partially_accepted_samples ?? 0} partial</span>
            <span>{preparation.no_eligible_term_samples ?? 0} without terms</span>
            <span>{preparation.rejected_term_count ?? 0} rejected terms</span>
            <span>{preparation.failed_samples} failed</span>
          </div>
        ) : preview ? (
          <div className="stt-benchmark-preview">
            <strong>{preview.sample_count} eligible audio files</strong>
            {preview.selected_sample_count != null ? <span>{preview.selected_sample_count} selected</span> : null}
            {preview.excluded_sample_count ? <span>{preview.excluded_sample_count} excluded</span> : null}
            <span>{preview.cell_count} provider cells</span>
            <span>{preview.audio_minutes} audio min</span>
            <span>{preview.keyterm_sample_count} with keyterms</span>
          </div>
        ) : (
          <span className="stt-muted">Preview freezes the exact sample manifest before starting.</span>
        )}
        <button
          className="stt-btn stt-btn--secondary"
          type="button"
          disabled={!canPreview || working}
          onClick={onPreview}
        >
          {working ? <Loader2 className="stt-spin" size={15} /> : <RefreshCw size={15} />}
          Preview
        </button>
        <button
          className="stt-btn stt-btn--primary"
          type="button"
          disabled={!preview || preview.sample_count === 0 || working}
          onClick={onStart}
        >
          <Play size={15} /> Start benchmark
        </button>
      </div>
      {preparation?.status === "completed" ? (
        <div className="stt-keyterm-preparation">
          <div className="stt-keyterm-preparation-summary">
            <strong>Frozen {keytermSourceLabel(preparationSource)}</strong>
            {generationContract?.prompt_version ? <span>Prompt {generationContract.prompt_version}</span> : null}
            {generationContract?.schema_version ? <span>Schema {generationContract.schema_version}</span> : null}
            {preparation.generation_contract_current === false && preparation.current_generation_contract?.prompt_version ? (
              <span>Current prompt {preparation.current_generation_contract.prompt_version}</span>
            ) : null}
            {preparation.generation_contract_current === false ? (
              <span className="stt-keyterm-version-warning">Generator version changed</span>
            ) : null}
            <strong>{preparation.eligible_samples} / {preparation.total_samples} eligible for benchmark</strong>
            <span>{Math.max(0, preparation.total_samples - preparation.eligible_samples)} excluded</span>
            <span>{preparation.term_count} terms</span>
            <span>{preparation.partially_accepted_samples ?? 0} partially accepted</span>
            <span>{preparation.no_eligible_term_samples ?? 0} without eligible terms</span>
            <span>{preparation.rejected_term_count ?? 0} rejected candidates</span>
            <span>{preparation.cached_samples} cache hits</span>
            <span>{preparation.failed_samples} failures</span>
            <span>${preparation.estimated_cost.toFixed(4)} extraction cost</span>
            {preparation.regeneration_available ? (
              <button
                className="stt-btn stt-btn--secondary"
                type="button"
                disabled={working}
                onClick={onRegenerate}
                title="Run extraction again and bypass cached results"
              >
                {working ? <Loader2 className="stt-spin" size={13} /> : <RotateCcw size={13} />}
                Regenerate
              </button>
            ) : null}
          </div>
          <div className="stt-keyterm-preparation-table">
            <table className="stt-table">
              <thead><tr><th>Sample</th><th>Approved reference</th><th>Accepted terms</th><th>Rejected candidates</th><th>Status</th></tr></thead>
              <tbody>
                {preparation.items.map((item) => {
                  const view = keytermPreparationItemView(item);
                  return (
                    <tr key={item.sample_id}>
                      <td className="stt-prov">{item.sample_id}</td>
                      <td>{item.reference_text}</td>
                      <td>
                        {item.terms.length
                          ? item.terms.map((term) => `${term.text}${term.category ? ` (${term.category.replaceAll("_", " ")})` : ""}`).join(" · ")
                          : "—"}
                        {!item.terms.length && item.no_terms_reason ? <small className="stt-keyterm-detail">{item.no_terms_reason}</small> : null}
                        {!item.terms.length && item.no_eligible_reason ? (
                          <small className="stt-keyterm-detail">Reason: {item.no_eligible_reason.replaceAll("_", " ")}</small>
                        ) : null}
                      </td>
                      <td>
                        {view.rejectedTerms.length ? (
                          <details className="stt-keyterm-rejections">
                            <summary>{view.rejectedTerms.length} rejected</summary>
                            <div>
                              {view.rejectedTerms.map((term, index) => (
                                <span key={`${term.attempt}-${term.text}-${index}`}>
                                  <strong>{term.text}</strong>
                                  <small>
                                    {term.rejection_code.replaceAll("_", " ")}
                                    {term.attempt ? ` · attempt ${term.attempt}` : ""}
                                  </small>
                                  {term.model_reason ? <small>{term.model_reason}</small> : null}
                                </span>
                              ))}
                            </div>
                          </details>
                        ) : "—"}
                      </td>
                      <td>
                        <span className={`stt-keyterm-outcome stt-keyterm-outcome--${view.outcome}`}>{view.statusLabel}</span>
                        {item.error ? <small className="stt-keyterm-detail">{item.error}</small> : null}
                        {item.prompt ? (
                          <details className="stt-keyterm-prompt">
                            <summary>View generated prompt</summary>
                            <pre>{item.prompt}</pre>
                            <small>
                              {[item.prompt_version && `Prompt ${item.prompt_version}`, item.schema_version && `Schema ${item.schema_version}`, item.policy_version && `Policy ${item.policy_version}`]
                                .filter(Boolean)
                                .join(" · ")}
                            </small>
                          </details>
                        ) : null}
                        {(item.source_used?.length || item.source_hash || item.attempts) ? (
                          <details className="stt-keyterm-prompt">
                            <summary>View generation diagnostics</summary>
                            <small>
                              Source: {item.source_used?.join(" + ") || "unavailable"}
                            </small>
                            {item.source_hash ? <small>Context hash: {item.source_hash}</small> : null}
                            <small>
                              Attempts: {item.attempts ?? 0}
                              {item.response_ids?.length ? ` · Responses: ${item.response_ids.join(", ")}` : ""}
                            </small>
                            {item.source_context_summary ? (
                              <pre>{JSON.stringify(item.source_context_summary, null, 2)}</pre>
                            ) : null}
                            {item.attempt_history?.length ? (
                              <pre>{JSON.stringify(item.attempt_history, null, 2)}</pre>
                            ) : null}
                          </details>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function RunHistory({
  runs,
  selectedId,
  onSelect,
  onRefresh,
}: {
  runs: BenchmarkRun[];
  selectedId: string | null;
  onSelect: (run: BenchmarkRun) => void;
  onRefresh: () => void;
}) {
  return (
    <aside className="stt-run-history">
      <div className="stt-benchmark-section-head">
        <h3>Run history</h3>
        <button className="stt-icon-btn" type="button" title="Refresh runs" onClick={onRefresh}>
          <RefreshCw size={14} />
        </button>
      </div>
      {runs.length ? (
        <div className="stt-run-list">
          {runs.map((run) => {
            const splits = run.config.splits.map(shortSplit).join(", ") || "—";
            return (
              <button
                key={run.run_id}
                type="button"
                className={selectedId === run.run_id ? "active" : ""}
                title={`${run.config.dataset_version} · ${run.config.splits.map(formatSplit).join(", ")} · ${run.sample_count} samples · ${run.config.provider_keys.length} providers · ${dateTime(run.created_at)}`}
                onClick={() => onSelect(run)}
              >
                <span>
                  <strong>{run.config.dataset_version}</strong>
                  <small>{splits} · {run.sample_count} samples</small>
                  <small>{dateTime(run.created_at)}</small>
                </span>
                <RunStatus run={run} />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="stt-empty">No benchmark runs yet.</div>
      )}
    </aside>
  );
}

function JudgeCalibrationPanel({
  runs,
  calibrations,
  working,
  onChanged,
  onError,
}: {
  runs: BenchmarkRun[];
  calibrations: JudgeCalibration[];
  working: boolean;
  onChanged: () => void | Promise<void>;
  onError: (cause: unknown) => void;
}) {
  const [phase, setPhase] = useState<"development" | "validation">("development");
  const [judgePolicyVersion, setJudgePolicyVersion] = useState("semantic-preservation-v10");
  const [sourceRunId, setSourceRunId] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedCalibrationId, setSelectedCalibrationId] = useState<string | null>(null);
  const [items, setItems] = useState<JudgeCalibrationItem[]>([]);
  const completedRuns = runs.filter((run) => run.status === "completed");

  const create = async () => {
    const source = sourceRunId || completedRuns[0]?.run_id;
    if (!source) return;
    setBusy(true);
    try {
      await arenaApi.createJudgeCalibration(source, phase);
      onChanged();
    } catch (cause) {
      onError(cause);
    } finally {
      setBusy(false);
    }
  };

  const evaluate = async (calibrationId: string) => {
    setBusy(true);
    try {
      await arenaApi.evaluateJudgeCalibration(calibrationId, { policy_version: judgePolicyVersion });
      onChanged();
    } catch (cause) {
      onError(cause);
    } finally {
      setBusy(false);
    }
  };

  const openQueue = async (calibrationId: string) => {
    setBusy(true);
    try {
      const response = await arenaApi.judgeCalibrationItems(calibrationId, true);
      setSelectedCalibrationId(calibrationId);
      setItems(response.items);
    } catch (cause) {
      onError(cause);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (calibration: JudgeCalibration) => {
    if (!confirm(`Archive the ${calibration.phase} calibration set? Its review data will be retained.`)) return;
    setBusy(true);
    try {
      await arenaApi.archiveJudgeCalibration(calibration.calibration_id);
      if (selectedCalibrationId === calibration.calibration_id) {
        setSelectedCalibrationId(null);
        setItems([]);
      }
      await onChanged();
    } catch (cause) {
      onError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="stt-calibration" aria-labelledby="judge-calibration-title">
      <div className="stt-evaluator-head">
        <div>
          <h4 id="judge-calibration-title">Judge calibration</h4>
          <p>Human-adjudicated calibration is required before an official provider recommendation can be released.</p>
        </div>
        <div className="stt-calibration-actions">
          <select className="stt-select" value={sourceRunId} onChange={(event) => setSourceRunId(event.target.value)}>
            <option value="">Latest completed run</option>
            {completedRuns.map((run) => <option key={run.run_id} value={run.run_id}>{run.config.dataset_version} · {formatSplit(run.config.splits[0] ?? "")}</option>)}
          </select>
          <select className="stt-select" value={phase} onChange={(event) => setPhase(event.target.value as typeof phase)}>
            <option value="development">Development (300 target)</option>
            <option value="validation">Validation (200 target)</option>
          </select>
          <select className="stt-select" aria-label="Calibration judge contract" value={judgePolicyVersion} onChange={(event) => setJudgePolicyVersion(event.target.value)}>
            {JUDGE_POLICY_OPTIONS.map(({ value, label, disabled }) => (
              <option key={value} value={value} disabled={disabled}>{label}</option>
            ))}
          </select>
          <button className="stt-btn stt-btn--secondary" type="button" disabled={working || busy || !completedRuns.length} onClick={create}>
            <FlaskConical size={14} /> Create set
          </button>
        </div>
      </div>
      {calibrations.length ? (
        <div className="stt-calibration-list">
          {calibrations.slice(0, 4).map((calibration) => {
            const summary = calibration.summary;
            return <div key={calibration.calibration_id} className="stt-calibration-row">
              <span><strong>{calibration.phase}</strong><small>{calibration.item_count} frozen cells · {calibration.status}</small></span>
              <span><small>Material recall</small><strong>{pct(summary?.material_error_recall)}</strong></span>
              <span><small>Equivalent precision</small><strong>{pct(summary?.equivalent_precision)}</strong></span>
              <span><small>Critical recall</small><strong>{pct(summary?.critical_error_recall)}</strong></span>
              <span className={`stt-run-status ${summary?.passed ? "stt-run-status--completed" : "stt-run-status--interrupted"}`}>{summary?.passed ? "passed" : "pending"}</span>
              <button className="stt-icon-btn" type="button" title="Open blinded calibration review queue" disabled={working || busy} onClick={() => openQueue(calibration.calibration_id)}>
                <Check size={14} />
              </button>
              <button className="stt-icon-btn" type="button" title="Run judge against frozen calibration set" disabled={working || busy} onClick={() => evaluate(calibration.calibration_id)}>
                {busy ? <Loader2 className="stt-spin" size={14} /> : <Play size={14} />}
              </button>
              <button className="stt-icon-btn" type="button" title="Archive calibration set" aria-label="Archive calibration set" disabled={working || busy || calibration.status === "passed"} onClick={() => archive(calibration)}>
                <Trash2 size={14} />
              </button>
            </div>;
          })}
        </div>
      ) : <div className="stt-muted stt-calibration-empty">No calibration set yet. Create development calibration before relying on recommendations.</div>}
      {selectedCalibrationId ? <CalibrationReviewQueue
        calibrationId={selectedCalibrationId}
        items={items}
        onChanged={() => openQueue(selectedCalibrationId)}
        onError={onError}
      /> : null}
    </section>
  );
}

function CalibrationReviewQueue({
  calibrationId,
  items,
  onChanged,
  onError,
}: {
  calibrationId: string;
  items: JudgeCalibrationItem[];
  onChanged: () => void | Promise<void>;
  onError: (cause: unknown) => void;
}) {
  const [notes, setNotes] = useState("");
  const [severity, setSeverity] = useState<"none" | "non_critical" | "critical" | "unknown">("non_critical");
  const [adjudicationVerdict, setAdjudicationVerdict] = useState<"equivalent" | "material_error" | "unclear">("material_error");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const activeItem = items[0];
  if (!activeItem) return <div className="stt-calibration-empty">No calibration cells are waiting for review.</div>;
  const submit = async (verdict: "equivalent" | "material_error" | "unclear", adjudicate = false) => {
    setSaving(true);
    try {
      const nextSeverity = verdict === "equivalent" ? "none" : verdict === "unclear" ? "unknown" : severity;
      if (adjudicate) {
        await arenaApi.adjudicateJudgeCalibrationItem(calibrationId, activeItem.item_id, verdict, nextSeverity, notes || null);
      } else {
        await arenaApi.reviewJudgeCalibrationItem(calibrationId, activeItem.item_id, verdict, nextSeverity, notes || null);
      }
      setNotes("");
      setSeverity("non_critical");
      setFeedback(adjudicate ? "Adjudication saved." : "Review saved. Waiting for an independent reviewer.");
      await onChanged();
    } catch (cause) {
      onError(cause);
    } finally {
      setSaving(false);
    }
  };
  return <article className="stt-calibration-review">
    <header><strong>Blinded calibration cell</strong><span>{activeItem.review_count} independent reviews</span><AudioPlayer uri={activeItem.sample.audio_uri} compact /></header>
    <div className="stt-benchmark-transcripts"><div><span>Frozen reference</span><p>{activeItem.sample.reference_text || "No speech"}</p></div><div><span>Transcript candidate</span><p>{activeItem.hypothesis || "(empty)"}</p></div></div>
    {feedback ? <div className="stt-review-feedback" role="status">{feedback}</div> : null}
    <div className="stt-review-controls"><input className="stt-input" value={notes} placeholder="Review note (optional)" maxLength={2000} onChange={(event) => setNotes(event.target.value)} /><select className="stt-select" value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)}><option value="non_critical">Non-critical</option><option value="critical">Critical</option><option value="unknown">Unknown</option><option value="none">No error</option></select><button type="button" disabled={saving} onClick={() => submit("equivalent")}><Check size={14} /> Equivalent</button><button type="button" disabled={saving} onClick={() => submit("material_error")}><X size={14} /> Material error</button><button type="button" disabled={saving} onClick={() => submit("unclear")}><AlertTriangle size={14} /> Unclear</button>{activeItem.review_count >= 2 ? <><select className="stt-select" value={adjudicationVerdict} onChange={(event) => setAdjudicationVerdict(event.target.value as typeof adjudicationVerdict)} aria-label="Adjudication verdict"><option value="equivalent">Equivalent</option><option value="material_error">Material error</option><option value="unclear">Unclear</option></select><button type="button" disabled={saving} onClick={() => submit(adjudicationVerdict, true)}><FlaskConical size={14} /> Adjudicate</button></> : null}</div>
  </article>;
}

function RunStatus({ run }: { run: BenchmarkRun }) {
  const progress = run.total_cells
    ? Math.round((run.completed_cells / run.total_cells) * 100)
    : 0;
  return (
    <span className={`stt-run-status stt-run-status--${run.status}`}>
      {run.status === "running" ? `${progress}%` : run.status.replaceAll("_", " ")}
    </span>
  );
}

function JudgeEvaluationPanel({
  run,
  evaluations,
  selected,
  selectedId,
  requestedPolicyVersion,
  working,
  onChange,
  onRequestedPolicyChange,
  onCreate,
  onActivate,
}: {
  run: BenchmarkRun;
  evaluations: BenchmarkJudgeEvaluationContext[];
  selected: BenchmarkJudgeEvaluationContext | null;
  selectedId: string;
  requestedPolicyVersion: string;
  working: boolean;
  onChange: (evaluationId: string) => void;
  onRequestedPolicyChange: (policyVersion: string) => void;
  onCreate: () => void;
  onActivate: () => void;
}) {
  const holdout = run.config.splits.includes("locked_holdout");
  const progress = selected?.total_cells
    ? ((selected.completed_cells ?? 0) / selected.total_cells) * 100
    : 0;
  const calibrated = selected?.summary?.policy?.calibration.passed;
  const activationBlocked = CALIBRATION_REQUIRED_POLICIES.has(selected?.policy_version ?? "") && !calibrated;
  const transitions = Object.entries(selected?.disagreements?.transitions ?? {});

  return (
    <section className="stt-judge-evaluation" aria-labelledby="judge-evaluation-title">
      <div className="stt-judge-evaluation-head">
        <div>
          <h4 id="judge-evaluation-title">Judge evaluation</h4>
          <p>Switch analytics between immutable judge versions without rerunning STT.</p>
        </div>
        <div className="stt-run-actions">
          <select
            className="stt-select"
            aria-label="Judge evaluation"
            value={selectedId}
            onChange={(event) => onChange(event.target.value)}
          >
            {evaluations.map((evaluation) => (
              <option key={evaluation.evaluation_id} value={evaluation.evaluation_id}>
                {evaluation.evaluation_id === "legacy" ? "Original" : `${evaluation.model ?? "Judge"} · ${evaluation.prompt_version ?? "unknown"}`}
                {evaluation.active ? " · active" : ""}
                {evaluation.status !== "completed" ? ` · ${evaluation.status}` : ""}
              </option>
            ))}
          </select>
          <select
            className="stt-select"
            aria-label="Judge policy for re-evaluation"
            value={requestedPolicyVersion}
            onChange={(event) => onRequestedPolicyChange(event.target.value)}
          >
            {JUDGE_POLICY_OPTIONS.map((policy) => (
              <option key={policy.value} value={policy.value} disabled={policy.disabled}>{policy.label}</option>
            ))}
          </select>
          <button
            className="stt-btn stt-btn--secondary"
            type="button"
            disabled={working || holdout || run.status !== "completed"}
            title={holdout ? "Evaluation holdout runs cannot be re-evaluated." : `Create or resume ${requestedPolicyVersion} without rerunning STT`}
            onClick={onCreate}
          >
            <FlaskConical size={15} /> Re-evaluate
          </button>
          {selected && selected.evaluation_id !== "legacy" ? (
            <button
              className="stt-btn stt-btn--primary"
              type="button"
              disabled={working || selected.status !== "completed" || selected.active || activationBlocked}
              title={activationBlocked ? "Activation requires passed validation calibration for selected judge contract." : "Use this immutable evaluation as the run's default analytics"}
              onClick={onActivate}
            >
              <Play size={15} /> {selected.active ? "Active" : "Activate"}
            </button>
          ) : null}
        </div>
      </div>
      {selected ? (
        <>
          <div className="stt-judge-evaluation-meta">
            <span><small>Model</small><strong>{selected.model ?? "Legacy configuration"}</strong></span>
            <span><small>Prompt</small><strong>{selected.prompt_version ?? "—"}</strong></span>
            <span><small>Reasoning</small><strong>{selected.reasoning_effort ?? "deterministic sampling"}</strong></span>
            <span><small>Original disagreements</small><strong>{selected.disagreements ? `${selected.disagreements.total} / ${selected.disagreements.compared}` : "—"}</strong></span>
            <span><small>Unavailable</small><strong>{selected.unavailable_cells ?? "—"}</strong></span>
            <span><small>Judge cost</small><strong>{selected.estimated_cost == null ? "—" : `$${selected.estimated_cost.toFixed(4)}`}</strong></span>
            <span>
              <small>Recommendation gate</small>
              <strong>{calibrated ? "calibrated" : "provisional"}</strong>
            </span>
          </div>
          {["queued", "running"].includes(selected.status) ? (
            <div className="stt-run-progress">
              <div style={{ width: `${progress}%` }} />
              <span>{selected.completed_cells ?? 0} / {selected.total_cells ?? 0} judgments</span>
            </div>
          ) : null}
          {selected.failure_reason ? <div className="stt-empty stt-empty--err">{selected.failure_reason}</div> : null}
          {transitions.length ? (
            <p className="stt-judge-evaluation-transitions">
              {transitions.map(([transition, count]) => `${transition.replace("->", " → ")}: ${count}`).join(" · ")}
            </p>
          ) : null}
          {!calibrated ? (
            <p className="stt-judge-evaluation-warning">
              <Info size={13} /> This exact model and prompt have not passed validation calibration. LLM-judge decisions remain visible; provider recommendation stays blocked.
            </p>
          ) : null}
        </>
      ) : <div className="stt-empty">Loading judge evaluation…</div>}
    </section>
  );
}

function ReferenceReviewPanel({
  runId,
  onReviewed,
}: {
  runId: string;
  onReviewed: () => Promise<void>;
}) {
  const [items, setItems] = useState<BenchmarkReferenceReviewItem[]>([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const page = await arenaApi.benchmarkReferenceReviews(runId, "unknown");
      setItems(page.items);
      setError(null);
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    arenaApi.benchmarkReferenceReviews(runId, "unknown")
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setError(null);
      })
      .catch((cause) => {
        if (!cancelled) setError(formatError(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const active = items[0];
  const submit = async (quality: "valid" | "suspect") => {
    if (!active) return;
    if (quality === "suspect" && !notes.trim()) {
      setError("Add note explaining why approved reference is suspect.");
      return;
    }
    setSaving(true);
    try {
      await arenaApi.reviewBenchmarkReference(
        runId,
        active.sample_id,
        quality,
        notes.trim() || null,
      );
      setNotes("");
      await Promise.all([refresh(), onReviewed()]);
      toast.success(`Reference marked ${quality}`);
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <details className="stt-reference-review">
      <summary>
        <span>Reference quality review</span>
        <small>{loading ? "Loading…" : `${items.length} provider-blind references pending`}</small>
      </summary>
      <div className="stt-reference-review-body">
        <p>Optional reference-quality diagnostic. LLM-judge scoring does not wait for this review; inspect approved reference against audio and bounded context only.</p>
        {error ? <div className="stt-empty stt-empty--err">{error}</div> : null}
        {!loading && !active ? <div className="stt-empty">No unknown references remain.</div> : null}
        {active ? (
          <article>
            <header>
              <strong>{active.sample_id}</strong>
              <AudioPlayer uri={active.audio_uri} compact />
            </header>
            <div className="stt-reference-review-copy">
              <span>Approved reference</span>
              <p>{active.reference_text}</p>
              {active.context_text ? <small>Bounded context: {active.context_text}</small> : null}
            </div>
            <div className="stt-review-controls">
              <input className="stt-input" value={notes} maxLength={2000} placeholder="Note required when marking suspect" onChange={(event) => setNotes(event.target.value)} />
              <button type="button" disabled={saving} onClick={() => submit("valid")}><Check size={14} /> Reference valid</button>
              <button type="button" disabled={saving} onClick={() => submit("suspect")}><AlertTriangle size={14} /> Reference suspect</button>
            </div>
          </article>
        ) : null}
      </div>
    </details>
  );
}

function RunDetail({
  run,
  rows,
  rowsLoading,
  judgeEvaluations,
  selectedJudgeEvaluation,
  selectedJudgeEvaluationId,
  requestedJudgePolicyVersion,
  offset,
  providerFilter,
  decisionFilter,
  reviewFilter,
  errorTypeFilter,
  keytermModeFilter,
  keytermSourceFilter,
  unclearReasonFilter,
  referenceQualityFilter,
  working,
  onProviderFilter,
  onDecisionFilter,
  onReviewFilter,
  onErrorTypeFilter,
  onErrorDrillDown,
  onKeytermModeFilter,
  onKeytermSourceFilter,
  onUnclearReasonFilter,
  onReferenceQualityFilter,
  onClearFilters,
  onPage,
  onCancel,
  onResume,
  onRebuild,
  onJudgeEvaluationChange,
  onRequestedJudgePolicyChange,
  onCreateJudgeEvaluation,
  onActivateJudgeEvaluation,
  cellReviewRefreshing,
  onRefreshCells,
  onReviewed,
}: {
  run: BenchmarkRun | null;
  rows: BenchmarkRowsPage | null;
  rowsLoading: boolean;
  judgeEvaluations: BenchmarkJudgeEvaluationContext[];
  selectedJudgeEvaluation: BenchmarkJudgeEvaluationContext | null;
  selectedJudgeEvaluationId: string;
  requestedJudgePolicyVersion: string;
  offset: number;
  providerFilter: string;
  decisionFilter: string;
  reviewFilter: string;
  errorTypeFilter: string;
  keytermModeFilter: string;
  keytermSourceFilter: string;
  unclearReasonFilter: string;
  referenceQualityFilter: string;
  working: boolean;
  onProviderFilter: (value: string) => void;
  onDecisionFilter: (value: string) => void;
  onReviewFilter: (value: string) => void;
  onErrorTypeFilter: (value: string) => void;
  onErrorDrillDown: (provider: string, errorType: string) => void;
  onKeytermModeFilter: (value: string) => void;
  onKeytermSourceFilter: (value: string) => void;
  onUnclearReasonFilter: (value: string) => void;
  onReferenceQualityFilter: (value: string) => void;
  onClearFilters: () => void;
  onPage: (offset: number) => void;
  onCancel: () => void;
  onResume: () => void;
  onRebuild: () => void;
  onJudgeEvaluationChange: (evaluationId: string) => void;
  onRequestedJudgePolicyChange: (policyVersion: string) => void;
  onCreateJudgeEvaluation: () => void;
  onActivateJudgeEvaluation: () => void;
  cellReviewRefreshing: boolean;
  onRefreshCells: () => Promise<void>;
  onReviewed: () => Promise<void>;
}) {
  const [scoreTrack, setScoreTrack] = useState<ScoreTrack>("judge_only");
  const [activeView, setActiveView] = useState<BenchmarkView>("overview");
  if (!run) return <main className="stt-run-detail stt-empty">Select or start a benchmark run.</main>;
  const providerEntries = Object.entries(run.summary?.providers ?? {});
  const evaluator = run.summary?.evaluator;
  const decision = run.summary?.decision;
  const coverage = run.summary?.coverage;
  const pairwise = run.summary?.pairwise ?? [];
  const isV3 = (run.summary?.summary_version ?? 0) >= 3;
  const policy = run.summary?.policy;
  const keytermTrack = run.summary?.keyterm_track;
  const hintVariants = run.summary?.hint_variants;
  const hintVariantCellCounts = benchmarkHintVariantCellCounts(run.summary ?? {});
  const publishedHintRows = Object.entries(hintVariants?.providers ?? {}).flatMap(
    ([provider, summary]) => Object.entries(summary.variants)
      .filter(([variant]) => variant !== "baseline")
      .map(([variant, result]) => ({ provider, variant, result }))
      .filter((row): row is typeof row & { result: NonNullable<typeof row.result> } => Boolean(row.result)),
  );
  const judgeOnlyTrack = run.summary?.judge_only;
  const judgeHealth = run.summary?.judge_health;
  const officialJudge = run.summary?.official_judge;
  const judgeEligible = officialJudge?.eligible
    ?? judgeHealth?.official_metrics_eligible
    ?? false;
  const judgeBlockingReasons = judgeHealth?.blocking_reasons ?? [];
  const errorTypes = [...new Set([
    ...REVIEW_ERROR_TYPES,
    ...providerEntries
      .flatMap(([, summary]) => Object.keys(summary.error_types ?? {}))
      .filter((value) => REVIEW_ERROR_TYPES.includes(value)),
  ])];
  const blindAudit = reviewFilter === "audit";
  const progress = run.total_cells ? (run.completed_cells / run.total_cells) * 100 : 0;
  const keytermProviderResults = Object.values(keytermTrack?.providers ?? {});
  const keytermEffectReasons = keytermProviderResults
    .map((result) => result.effect_reason)
    .filter(Boolean)
    .join(" ");
  const additionalKeytermClusters = Math.max(
    0,
    ...keytermProviderResults.map(
      (result) => result.additional_session_clusters_estimate ?? 0,
    ),
  );
  const advancedFilterCount = [
    errorTypeFilter,
    keytermModeFilter,
    keytermSourceFilter,
    unclearReasonFilter,
    referenceQualityFilter,
  ].filter(Boolean).length;
  const rowFilterCount = [
    providerFilter,
    decisionFilter,
    reviewFilter,
    errorTypeFilter,
    keytermModeFilter,
    keytermSourceFilter,
    unclearReasonFilter,
    referenceQualityFilter,
  ].filter(Boolean).length;
  const keytermModeOptions = cellReviewKeytermModeOptions(run.config.keyterm_source);
  const hasOverviewAnalytics = Boolean(decision || coverage || providerEntries.length);
  const hasPerformanceAnalytics = Boolean(
    keytermTrack || hintVariants || policy || providerEntries.length || evaluator || pairwise.length,
  );

  return (
    <main className="stt-run-detail">
      <div className="stt-benchmark-section-head">
        <div>
          <h3>{run.config.dataset_version} benchmark</h3>
          <p>{run.config.splits.join(", ")} · {run.sample_count} samples · created by {run.created_by}</p>
        </div>
        <div className="stt-run-actions">
          <RunStatus run={run} />
          {ACTIVE.has(run.status) ? (
            <button className="stt-btn stt-btn--secondary" type="button" disabled={working} onClick={onCancel}>
              <PauseCircle size={15} /> Cancel
            </button>
          ) : null}
          {["interrupted", "failed"].includes(run.status) ? (
            <button className="stt-btn stt-btn--primary" type="button" disabled={working} onClick={onResume}>
              <RotateCcw size={15} /> Resume
            </button>
          ) : null}
          {!ACTIVE.has(run.status) ? (
            <button className="stt-btn stt-btn--secondary" type="button" disabled={working} onClick={onRebuild}>
              <RefreshCw size={15} /> Rebuild analytics
            </button>
          ) : null}
        </div>
      </div>

      {ACTIVE.has(run.status) ? (
        <div className="stt-run-progress">
          <div style={{ width: `${progress}%` }} />
          <span>{run.completed_cells} / {run.total_cells} cells</span>
        </div>
      ) : null}
      {run.failure_reason ? <div className="stt-empty stt-empty--err">{run.failure_reason}</div> : null}

      {!judgeEligible ? (
        <div className="stt-benchmark-decision stt-benchmark-decision--pending" role="status">
          <strong>Official analytics blocked: LLM-judge health gates incomplete</strong>
          <span>{judgeBlockingReasons.length ? judgeBlockingReasons.map(judgeGateLabel).join(" · ") : `Complete ${run.summary?.judge_evaluation?.policy_version ?? "active judge contract"} calibration before using provider ordering.`}</span>
          <span>Rows still receive Luna decisions. Human review is optional diagnostic evidence.</span>
        </div>
      ) : null}

      <nav className="stt-run-view-tabs" aria-label="Benchmark views">
        {([
          ["overview", "Overview"],
          ["judge", "Judge health"],
          ["cells", "Cell review"],
          ["performance", "Performance"],
          ["calibration", "Calibration"],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={activeView === value ? "active" : ""}
            aria-current={activeView === value ? "page" : undefined}
            onClick={() => setActiveView(value)}
          >
            {label}
          </button>
        ))}
      </nav>

      {activeView === "calibration" ? (
        <JudgeEvaluationPanel
          run={run}
          evaluations={judgeEvaluations}
          selected={selectedJudgeEvaluation}
          selectedId={selectedJudgeEvaluationId}
          requestedPolicyVersion={requestedJudgePolicyVersion}
          working={working}
          onChange={onJudgeEvaluationChange}
          onRequestedPolicyChange={onRequestedJudgePolicyChange}
          onCreate={onCreateJudgeEvaluation}
          onActivate={onActivateJudgeEvaluation}
        />
      ) : null}

      {activeView === "cells" ? (
        <ReferenceReviewPanel
          key={run.run_id}
          runId={run.run_id}
          onReviewed={onReviewed}
        />
      ) : null}

      {activeView === "overview" ? <>
      {decision ? (
        <div className="stt-benchmark-decision">
          <strong>
            {decision.status === "pilot" ? `Pilot: ${decision.winner}` : decision.status === "retain_incumbent" ? `Retain ${policy?.baseline_provider ?? "incumbent"}` : decision.status === "collect_more_data" ? "Collect more data" : decision.winner ? `Candidate: ${decision.winner}` : "No clear winner"}
            {decision.provisional ? " · provisional" : ""}
          </strong>
          <span>{decision.reason}</span>
          {decision.additional_samples_required ? <span>{decision.additional_samples_required} additional clustered samples estimated for the target detectable effect.</span> : null}
          {decision.failed_gates && Object.values(decision.failed_gates).some((gates) => gates.length) ? <span>Blockers: {Object.entries(decision.failed_gates).filter(([, gates]) => gates.length).map(([provider, gates]) => `${provider}: ${gates.join(", ")}`).join(" · ")}</span> : null}
        </div>
      ) : null}

      {providerEntries.length ? (
        <section className="stt-analytics-panel" aria-labelledby="baseline-results-title">
          <div className="stt-evaluator-head">
            <div>
              <h4 id="baseline-results-title">Baseline results <span className="stt-badge stt-badge--ghost">Keyterms off</span></h4>
              <p>Primary provider comparison using unprompted transcripts. These rows feed official benchmark analytics when judge-health gates pass.</p>
            </div>
          </div>
          <div className="stt-table-wrap stt-benchmark-summary">
            <table className="stt-table">
              <thead>
              <tr>
                  <th>Provider</th>
                  <th className="num"><MetricLabel label="LLM-judge bad outcome" help="Material, unclear, or unavailable outcomes from pinned Luna judge evaluation." /></th>
                  <th className="num"><MetricLabel label="Material error" help="Luna material errors among decisive judge outcomes." /></th>
                  <th className="num"><MetricLabel label="Judge coverage" help="Equivalent or material Luna decisions divided by eligible speech cells." /></th>
                  <th className="num"><MetricLabel label="nWER" help="Normalized word error rate after benchmark text normalization. Lower means the transcript is closer to the approved label." /></th>
                  <th className="num"><MetricLabel label="Failures" help="Provider requests that failed instead of returning a transcript. Lower, ideally zero, is better." /></th>
                  <th className="num"><MetricLabel label="p95 finalize" help="95th percentile time until the provider finalizes the transcript. Lower reduces turn-end latency." /></th>
                  <th><MetricLabel label="Gates" help="Eligibility checks for latency, reliability, keyterm regression, and false critical-term insertion." /></th>
                  <th><MetricLabel label="Gains" help="Keyterm lift is shown in the keyterm-enabled table. Baseline has no prompted comparison." /></th>
                </tr>
              </thead>
              <tbody>
                {providerEntries.map(([provider, summary]) => {
                  const judge = judgeOnlyProviderView(summary);
                  return (
                    <tr key={provider}>
                      <td className="stt-prov">{provider}</td>
                      <td className="num">
                        <PercentWithCi value={judge.badOrUnresolvedRate} ci={judge.summary?.bad_or_unresolved_rate_ci95 ?? null} />
                      </td>
                      <td className="num"><PercentWithCi value={judge.materialErrorRate} ci={judge.summary?.material_error_rate_ci95 ?? null} /><small className="stt-ci">{judge.materialErrors ?? "—"} / {judge.decisiveCells ?? "—"}</small></td>
                      <td className="num"><PercentWithCi value={judge.summary?.decisive_coverage ?? null} ci={judge.summary?.decisive_coverage_ci95 ?? null} /></td>
                      <td className="num">{pct(summary.mean_nwer)}</td>
                      <td className="num">{summary.failures} / {summary.cells}</td>
                      <td className="num">{ms(summary.p95_finalization_ms)}</td>
                      <td>
                        <span className={`stt-run-status ${summary.eligible ? "stt-run-status--completed" : "stt-run-status--failed"}`}>
                          {summary.eligible ? "eligible" : "failed"}
                        </span>
                      </td>
                      <td className="num">—</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {keytermTrack ? (
        <section className="stt-analytics-panel" aria-labelledby="keyterm-results-title">
          <div className="stt-evaluator-head">
            <div>
              <h4 id="keyterm-results-title">Keyterm-enabled results <span className="stt-badge stt-badge--noisy">Diagnostic only</span></h4>
              <p>Same provider columns as baseline. Keyterm-on values come from paired prompted cells; Gains shows reference-term recall lift versus paired baseline. Diagnostic only.</p>
            </div>
          </div>
          <div className="stt-table-wrap">
            <table className="stt-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th className="num"><MetricLabel label="LLM-judge bad outcome" help="Material, unclear, or unavailable outcomes from keyterm-enabled prompted cells." /></th>
                  <th className="num"><MetricLabel label="Material error" help="Luna material errors among decisive keyterm-enabled outcomes." /></th>
                  <th className="num"><MetricLabel label="Judge coverage" help="Equivalent or material Luna decisions divided by eligible keyterm-enabled speech cells." /></th>
                  <th className="num"><MetricLabel label="nWER" help="Absolute normalized word error rate for keyterm-enabled prompted cells." /></th>
                  <th className="num"><MetricLabel label="Failures" help="Keyterm-enabled provider requests that failed instead of returning a transcript." /></th>
                  <th className="num"><MetricLabel label="p95 finalize" help="95th percentile finalization latency for keyterm-enabled prompted cells." /></th>
                  <th><MetricLabel label="Gates" help="Keyterm-enabled rows are diagnostic and do not participate in official provider gates." /></th>
                  <th><MetricLabel label="Gains" help="Paired keyterm-on versus baseline improvement. Positive percentage points mean fewer bad or material outcomes; positive milliseconds mean lower finalization latency." /></th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(keytermTrack.providers).map(([provider, result]) => {
                  const keytermOn = result.keyterm_on;
                  const judge = keytermOn?.judge_only;
                  return (
                    <tr key={provider}>
                      <td className="stt-prov">{provider}</td>
                      <td className="num">{judge ? <PercentWithCi value={judge.bad_or_unresolved_rate} ci={judge.bad_or_unresolved_rate_ci95} /> : "—"}</td>
                      <td className="num">{judge ? <><PercentWithCi value={judge.material_error_rate} ci={judge.material_error_rate_ci95} /><small className="stt-ci">{judge.material_errors} / {judge.decisive_cells}</small></> : "—"}</td>
                      <td className="num">{judge ? <PercentWithCi value={judge.decisive_coverage} ci={judge.decisive_coverage_ci95} /> : "—"}</td>
                      <td className="num">{keytermOn ? pct(keytermOn.mean_nwer) : "—"}</td>
                      <td className="num">{keytermOn ? `${keytermOn.failures} / ${keytermOn.cells}` : "—"}</td>
                      <td className="num">{keytermOn ? ms(keytermOn.p95_finalization_ms) : "—"}</td>
                      <td><span className="stt-run-status stt-run-status--interrupted">diagnostic</span></td>
                      <td className="num">
                        <div className="stt-gain-list">
                          <GainMetric label="Bad" value={result.judge_bad_outcome_gain} ci={result.judge_bad_outcome_gain_ci95} />
                          <GainMetric label="Material" value={result.material_error_gain} ci={result.material_error_gain_ci95} />
                          <GainMetric label="p95" value={result.finalization_p95_gain_ms} ci={result.finalization_p95_gain_ci95} milliseconds />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {publishedHintRows.length ? (
        <section className="stt-analytics-panel" aria-labelledby="published-hint-results-title">
          <div className="stt-evaluator-head">
            <div>
              <h4 id="published-hint-results-title">Published-slide hint results <span className="stt-badge stt-badge--noisy">Diagnostic only</span></h4>
              <p>Prompted cells generated from published slide data. Baseline remains in the table above; provider capabilities determine which hint variants run.</p>
            </div>
          </div>
          <div className="stt-table-wrap">
            <table className="stt-table">
              <thead><tr>
                <th>Provider</th>
                <th>Variant</th>
                <th className="num"><MetricLabel label="LLM-judge bad outcome" help="Luna material, unclear, unavailable, and provider-failure outcomes among eligible prompted cells. Immutable judge result." /></th>
                <th className="num"><MetricLabel label="Material error" help="Human-resolved material errors where final review exists; otherwise the LLM-judge decision. The LLM count remains shown below as material errors / judged cells." /></th>
                <th className="num"><MetricLabel label="Judge coverage" help="Equivalent or material Luna decisions divided by paired prompted cells." /></th>
                <th className="num"><MetricLabel label="nWER" help="Absolute normalized word error rate for prompted cells." /></th>
                <th className="num"><MetricLabel label="Failures" help="Prompted provider requests that failed instead of returning a transcript." /></th>
                <th className="num"><MetricLabel label="p95 finalize" help="95th percentile finalization latency for prompted cells." /></th>
              </tr></thead>
              <tbody>{publishedHintRows.map(({ provider, variant, result }) => {
                const adjusted = result.audit_adjusted;
                return (
                  <tr key={`${provider}-${variant}`}>
                    <td className="stt-prov">{provider}</td>
                    <td>{variant}</td>
                    <td className="num">
                      <PercentWithCi value={result.llm_judge.bad_or_unresolved_rate} ci={null} />
                      <small className="stt-ci">{result.llm_judge.bad_or_unresolved_cells} / {result.paired_cell_count}</small>
                    </td>
                    <td className="num">
                      <PercentWithCi value={adjusted?.material_error_rate ?? result.llm_judge.material_error_rate} ci={null} />
                      <small className="stt-ci">
                        {adjusted
                          ? `${adjusted.material_errors} / ${adjusted.decisive_cells} resolved`
                          : `${benchmarkMaterialErrorCount(result.llm_judge) ?? "—"} / ${result.llm_judge.judged_cells} LLM`}
                      </small>
                      {adjusted && adjusted.human_reviewed_cells > 0 ? <small className="stt-ci">{adjusted.human_reviewed_cells} human reviewed</small> : null}
                    </td>
                    <td className="num"><PercentWithCi value={result.llm_judge.coverage} ci={null} /></td>
                    <td className="num">{pct(result.mean_nwer)}</td>
                    <td className="num">{result.failure_rate == null ? "—" : `${Math.round(result.failure_rate * result.cell_count)} / ${result.cell_count}`}</td>
                    <td className="num">{ms(result.mean_finalization_ms)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      ) : null}

      {coverage ? (
        <>
          <div className={`stt-analytics-overview ${hintVariantCellCounts.length ? "stt-analytics-overview--variants" : ""}`}>
            <span><MetricLabel label="Benchmark coverage" help="The frozen sample and provider-cell population used by this run. A cell is one provider on one selected sample and keyterm mode." /><strong>{coverage.sample_count} samples · {coverage.cell_count} cells</strong>{coverage.diagnostic_cell_count ? <small>{coverage.selection_cell_count} selection · {coverage.diagnostic_cell_count} diagnostic</small> : null}</span>
            {hintVariantCellCounts.length ? (
              <span>
                <MetricLabel label="Variant cells" help="Stored provider cells grouped by benchmark variant. Baseline sends no hints; other variants are independent treatments supported by each provider." />
                <span className="stt-variant-cell-counts" role="list" aria-label="Cell count by benchmark variant">
                  {hintVariantCellCounts.map(({ variant, label, count }) => (
                    <span key={variant} role="listitem"><small>{label}</small><strong>{count}</strong></span>
                  ))}
                </span>
              </span>
            ) : null}
            <span><MetricLabel label="Split" help="New benchmark runs use one split only. Development tunes, validation freezes, and Evaluation is protected from tuning." /><strong>{formatSplit(coverage.split)}</strong></span>
            <span><MetricLabel label="Speech / no speech" help="Speech samples contribute to transcription error metrics. No-speech samples are reported separately as detection accuracy and false-positive transcription." /><strong>{coverage.speech_samples} / {coverage.no_speech_samples}</strong></span>
          </div>
          {coverage.no_speech_samples === 0 ? (
            <p className="stt-judge-evaluation-warning">
              <Info size={13} /> No-speech coverage missing. No-speech noninferiority gate cannot pass for this run.
            </p>
          ) : null}
        </>
      ) : null}
      {!hasOverviewAnalytics ? <RunViewEmptyState view="Overview" run={run} /> : null}
      </> : null}

      {activeView === "judge" && providerEntries.length ? (
        <JudgeOnlyComparison
          providers={providerEntries}
          track={judgeOnlyTrack}
          blocked={!judgeEligible}
        />
      ) : null}

      {activeView === "judge" && (judgeHealth || evaluator || selectedJudgeEvaluation) ? (
        <JudgeHealthPanel
          health={judgeHealth}
          evaluator={evaluator}
          evaluation={selectedJudgeEvaluation ?? run.summary?.judge_evaluation ?? null}
        />
      ) : null}

      {activeView === "performance" ? (
        hasPerformanceAnalytics ? (
        <details className="stt-analytics-disclosure">
          <summary>
            <span>Performance diagnostics</span>
            <small>Keyterm effects, latency, pairwise results, and quality slices</small>
          </summary>
          <div className="stt-analytics-disclosure-body">
      {keytermTrack ? (
        <section className="stt-analytics-panel stt-keyterm-effect" aria-labelledby="keyterm-effect-title">
          <div className="stt-evaluator-head">
            <div>
              <h4 id="keyterm-effect-title">Reference-derived keyterm effect <span className="stt-badge stt-badge--noisy">Diagnostic only</span></h4>
              <p>Terms were extracted from approved references. Prompted cells estimate an upper bound and are excluded from provider selection, gates, and required audit.</p>
            </div>
          </div>
          <div className="stt-table-wrap">
            <table className="stt-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th className="num"><MetricLabel label="Pairs" help="Samples with both baseline and prompted results. At least 30 session clusters are required for an effect status." /></th>
                  <th className="num"><MetricLabel label="Term recall off" help="Share of frozen derived terms present in baseline transcripts." /></th>
                  <th className="num"><MetricLabel label="Term recall on" help="Share of frozen derived terms present when provider prompting is enabled." /></th>
                  <th className="num"><MetricLabel label="Recall lift" help="Prompted minus baseline term recall, with a session-clustered 95% confidence interval." /></th>
                  <th className="num"><MetricLabel label="nWER delta" help="Prompted minus baseline normalized word error. Negative values are improvements." /></th>
                  <th className="num"><MetricLabel label="Semantic delta" help="Prompted minus baseline provisional material-error rate." /></th>
                  <th className="num"><MetricLabel label="Failure delta" help="Prompted minus baseline provider failure rate, with a session-clustered 95% confidence interval." /></th>
                  <th className="num"><MetricLabel label="TTFT delta" help="Prompted minus baseline time to first transcript, with a session-clustered 95% confidence interval." /></th>
                  <th className="num"><MetricLabel label="Finalize delta" help="Prompted minus baseline finalization latency, with a session-clustered 95% confidence interval." /></th>
                  <th className="num"><MetricLabel label="W / T / L" help="Paired nWER wins, ties, and losses for prompted versus baseline transcripts." /></th>
                  <th>Term categories</th>
                  <th>Effect</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(keytermTrack.providers).map(([provider, result]) => (
                  <tr key={provider}>
                    <td className="stt-prov">{provider}</td>
                    <td className="num">{result.paired_samples}<small className="stt-ci">{result.paired_clusters} clusters</small></td>
                    <td className="num"><PercentWithCi value={result.term_recall_off} ci={result.term_recall_off_ci95} /></td>
                    <td className="num"><PercentWithCi value={result.term_recall_on} ci={result.term_recall_on_ci95} /></td>
                    <td className="num"><PercentWithCi value={result.term_recall_delta} ci={result.term_recall_delta_ci95} /></td>
                    <td className="num"><PercentWithCi value={result.nwer_delta} ci={result.nwer_delta_ci95} /></td>
                    <td className="num"><PercentWithCi value={result.semantic_error_delta} ci={result.semantic_error_delta_ci95} /></td>
                    <td className="num"><PercentWithCi value={result.failure_rate_delta} ci={result.failure_rate_delta_ci95} /></td>
                    <td className="num"><MillisecondsWithCi value={result.ttft_delta_ms} ci={result.ttft_delta_ci95} /></td>
                    <td className="num"><MillisecondsWithCi value={result.finalization_delta_ms} ci={result.finalization_delta_ci95} /></td>
                    <td className="num">{result.wins} / {result.ties} / {result.losses}</td>
                    <td>{result.term_count} terms<small className="stt-ci">{Object.entries(result.category_counts).map(([category, count]) => `${category.replaceAll("_", " ")} ${count}`).join(" · ") || "No categories"}</small></td>
                    <td><span className={`stt-run-status stt-run-status--${result.effect_status === "improves" ? "completed" : result.effect_status === "regresses" ? "failed" : "interrupted"}`}>{result.effect_status.replaceAll("_", " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="stt-muted">{keytermEffectReasons}{additionalKeytermClusters ? ` Estimated additional paired session clusters: ${additionalKeytermClusters}.` : ""}</p>
          <p className="stt-muted"><MetricLabel label="False insertion" help="Not applicable for this track because every prompted term is drawn from the approved reference. Measuring hallucination requires a separate distractor-term track." /> is not applicable.</p>
        </section>
      ) : null}

      {keytermTrack ? <KeytermPairPanel key={run.run_id} runId={run.run_id} providers={run.config.provider_keys} judgeEvaluationId={selectedJudgeEvaluationId} /> : null}

      {policy ? <div className="stt-policy-strip"><span><MetricLabel label="Baseline" help="Every candidate is compared directly with the production Deepgram baseline." /> <strong>{policy.baseline_provider}</strong></span><span><MetricLabel label="Quality margin" help="Candidate upper confidence bound must stay within this bad-outcome margin." /> <strong>{pct(policy.overall_noninferiority_margin)}</strong></span><span><MetricLabel label="Calibration" help="Final recommendations remain disabled until this model and prompt pass validation calibration." /> <strong>{policy.calibration.passed ? "passed" : "not passed"}</strong></span><span><MetricLabel label="Pricing" help="Provider cost is calculated from the immutable pricing version selected at run creation." /> <strong>{policy.pricing_version ?? "—"}</strong></span></div> : null}

      {providerEntries.length ? (
        <ErrorAnalytics
          providers={providerEntries}
          scoreTrack={scoreTrack}
          onScoreTrackChange={setScoreTrack}
          onDrillDown={onErrorDrillDown}
        />
      ) : null}

      {pairwise.length ? (
        <section className="stt-analytics-panel" aria-labelledby="pairwise-title">
          <div className="stt-evaluator-head">
            <h4 id="pairwise-title">Paired provider comparisons</h4>
            <p>Candidate delta is candidate minus Deepgram. Non-inferior means its upper confidence bound remains within the stated practical margin.</p>
          </div>
          <div className="stt-table-wrap">
            <table className="stt-table">
              <thead><tr><th>Comparison</th><th className="num">Cells</th><th className="num">Metric</th><th className="num">Delta</th><th className="num">95% CI</th><th>Decision</th></tr></thead>
              <tbody>{pairwise.map((item) => <tr key={`${item.left}-${item.right}`}>
                <td className="stt-prov">{item.left} vs {item.right}</td>
                <td className="num">{item.paired_cells}</td>
                <td className="num">{item.metric?.replaceAll("_", " ") ?? "semantic error"}</td>
                <td className="num">{pct(item.error_rate_delta ?? item.error_rate_improvement)}</td>
                <td className="num">{item.ci95 ? `${pct(item.ci95[0])} to ${pct(item.ci95[1])}` : "—"}</td>
                <td><span className={`stt-run-status stt-run-status--${item.verdict === "non_inferior" ? "completed" : item.verdict === "inferior" ? "failed" : "interrupted"}`}>{item.verdict?.replaceAll("_", " ") ?? "legacy"}</span></td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>
      ) : null}

      {isV3 && providerEntries.length ? (
        <SliceHeatmap
          providers={providerEntries}
          scoreTrack={scoreTrack}
          onScoreTrackChange={setScoreTrack}
        />
      ) : null}
          </div>
        </details>
      ) : <RunViewEmptyState view="Performance" run={run} />) : null}

      {activeView === "cells" ? <>
      <div className="stt-benchmark-row-toolbar">
        <select aria-label="Filter provider" className="stt-select" value={providerFilter} disabled={blindAudit} onChange={(event) => onProviderFilter(event.target.value)}>
          <option value="">All providers</option>
          {run.config.provider_keys.map((provider) => <option key={provider} value={provider}>{provider}</option>)}
        </select>
        <select aria-label="Filter judge decision" className="stt-select" value={decisionFilter} disabled={blindAudit} onChange={(event) => onDecisionFilter(event.target.value)}>
          <option value="">All judge decisions</option>
          <option value="equivalent">Equivalent</option>
          <option value="material_error">Material error</option>
          <option value="unclear">Unclear</option>
          <option value="unavailable">Unavailable</option>
          <option value="not_applicable">Not applicable</option>
          <option value="not_run">Not run</option>
        </select>
        <select aria-label="Filter review state" className="stt-select" value={reviewFilter} onChange={(event) => onReviewFilter(event.target.value)}>
          <option value="">All audit states</option>
          <option value="pending">Optional audit pending</option>
          <option value="audit">Blind audit queue</option>
          <option value="reviewed">Human audit completed</option>
        </select>
        <details className="stt-filter-disclosure">
          <summary>
            Advanced filters
            {advancedFilterCount ? <span>{advancedFilterCount}</span> : null}
          </summary>
          <div className="stt-filter-disclosure-body">
            <select aria-label="Filter error type" className="stt-select" value={errorTypeFilter} onChange={(event) => onErrorTypeFilter(event.target.value)}>
              <option value="">All error types</option>
              {errorTypes.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
            </select>
            <label className="stt-filter-field">
              <span>Variant</span>
              <select aria-label="Filter keyterm mode" className="stt-select" value={keytermModeFilter} disabled={blindAudit} onChange={(event) => onKeytermModeFilter(event.target.value)}>
                <option value="">All variants</option>
                {keytermModeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="stt-filter-field">
              <span>Hint source</span>
              <select aria-label="Filter keyterm source" className="stt-select" value={keytermSourceFilter} disabled={blindAudit} onChange={(event) => onKeytermSourceFilter(event.target.value)}>
                <option value="">All sources in this run</option>
                <option value={run.config.keyterm_source}>{keytermSourceLabel(run.config.keyterm_source)}</option>
              </select>
            </label>
            <select aria-label="Filter ambiguity reason" className="stt-select" value={unclearReasonFilter} onChange={(event) => onUnclearReasonFilter(event.target.value)}>
              <option value="">All ambiguity reasons</option>
              <option value="ambiguous_numeric_grouping">Ambiguous numeric grouping</option>
              <option value="unsupported_operator_insertion">Unsupported operator insertion</option>
              <option value="reference_quality_suspected">Reference quality suspected</option>
              <option value="insufficient_context">Insufficient context</option>
              <option value="other">Other</option>
            </select>
            <select aria-label="Filter reference quality" className="stt-select" value={referenceQualityFilter} onChange={(event) => onReferenceQualityFilter(event.target.value)}>
              <option value="">All reference quality</option>
              <option value="valid">Valid reference</option>
              <option value="suspect">Reference suspect</option>
              <option value="unknown">Unknown reference quality</option>
            </select>
          </div>
        </details>
        {rowFilterCount ? (
          <button type="button" className="stt-btn stt-btn--secondary" onClick={onClearFilters}>
            Clear filters
          </button>
        ) : null}
        <button
          type="button"
          className="stt-btn stt-btn--secondary"
          disabled={cellReviewRefreshing}
          onClick={() => onRefreshCells().catch(() => undefined)}
        >
          <RefreshCw size={14} className={cellReviewRefreshing ? "stt-spin" : undefined} />
          {cellReviewRefreshing ? "Refreshing cells…" : "Refresh cells"}
        </button>
        <span className="stt-result-count">{rowsLoading ? "Loading provider rows…" : `${rows?.total ?? 0} provider rows`}</span>
        <div className="stt-pager">
          <button type="button" disabled={rowsLoading || offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
          <span>{rowsLoading ? "Loading…" : rows?.total ? `${offset + 1}–${Math.min(offset + PAGE_SIZE, rows.total)}` : "0"}</span>
          <button type="button" disabled={rowsLoading || !rows || offset + PAGE_SIZE >= rows.total} onClick={() => onPage(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {run.config.keyterm_source === "slide_context" ? (
        <p className="stt-cell-variant-note">
          <Info size={13} aria-hidden="true" />
          Each sample and provider appears once per benchmark variant. Baseline is unprompted control; keywords, prompt, and combined rows are independent treatments.
        </p>
      ) : null}

      <div className="stt-benchmark-rows">
        {rowsLoading ? <div className="stt-empty">Loading cell page…</div> : rows?.items.map((row) => (
          <BenchmarkResultRow key={row.cell_id} runId={run.run_id} row={row} blindAudit={blindAudit} onReviewed={onReviewed} />
        ))}
        {!rowsLoading && rows && rows.items.length === 0 ? (
          <div className="stt-empty">
            <span>No rows match these filters.</span>
            {rowFilterCount ? <button type="button" className="stt-btn stt-btn--secondary" onClick={onClearFilters}>Clear filters</button> : null}
          </div>
        ) : null}
      </div>
      </> : null}
    </main>
  );
}

function KeytermPairPanel({
  runId,
  providers,
  judgeEvaluationId,
}: {
  runId: string;
  providers: string[];
  judgeEvaluationId: string;
}) {
  const [page, setPage] = useState<BenchmarkKeytermPairsPage | null>(null);
  const [provider, setProvider] = useState("");
  const [category, setCategory] = useState("");
  const [outcome, setOutcome] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 25;
  useEffect(() => {
    let cancelled = false;
    arenaApi.benchmarkKeytermPairs(runId, {
      providerKey: provider || undefined,
      category: category || undefined,
      outcome: outcome || undefined,
      judgeEvaluationId: judgeEvaluationId || undefined,
      limit,
      offset,
    }).then((result) => {
      if (!cancelled) setPage(result);
    }).catch(() => {
      if (!cancelled) setPage(null);
    });
    return () => { cancelled = true; };
  }, [runId, provider, category, outcome, judgeEvaluationId, offset]);
  const categories = page?.categories
    ?? [...new Set(page?.aggregates.map((item) => item.category) ?? [])];
  return (
    <section className="stt-analytics-panel stt-keyterm-pairs" aria-labelledby="keyterm-pairs-title">
      <div className="stt-evaluator-head">
        <div>
          <h4 id="keyterm-pairs-title">Keyterm pair drill-down <span className="stt-badge stt-badge--noisy">Diagnostic only</span></h4>
          <p>Each frozen term compares same provider and sample with prompting off versus on.</p>
        </div>
        <div className="stt-score-controls">
          <select className="stt-select" value={provider} onChange={(event) => { setProvider(event.target.value); setOffset(0); }}>
            <option value="">All providers</option>
            {providers.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select className="stt-select" value={category} onChange={(event) => { setCategory(event.target.value); setOffset(0); }}>
            <option value="">All categories</option>
            {categories.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
          </select>
          <select className="stt-select" value={outcome} onChange={(event) => { setOutcome(event.target.value); setOffset(0); }}>
            <option value="">All outcomes</option>
            <option value="gained">Gained</option>
            <option value="lost">Lost</option>
            <option value="retained">Retained</option>
            <option value="missed">Missed</option>
          </select>
        </div>
      </div>
      <div className="stt-table-wrap">
        <table className="stt-table">
          <thead><tr><th>Provider</th><th>Term</th><th>Category</th><th>Outcome</th><th className="num">nWER delta</th><th>Judge off / on</th><th>Reference</th></tr></thead>
          <tbody>{page?.items.length ? page.items.map((item) => (
            <tr key={`${item.provider_key}-${item.sample_id}-${item.term}`}>
              <td className="stt-prov">{item.provider_key}</td>
              <td>{item.term}</td>
              <td>{item.category.replaceAll("_", " ")}</td>
              <td><span className={`stt-run-status stt-run-status--${item.outcome === "gained" ? "completed" : item.outcome === "lost" ? "failed" : "interrupted"}`}>{item.outcome}</span></td>
              <td className="num">{pct(item.nwer_delta)}</td>
              <td>{item.judge_outcome.off ?? "—"} / {item.judge_outcome.on ?? "—"}</td>
              <td>{item.reference_quality}</td>
            </tr>
          )) : <tr><td colSpan={7} className="stt-muted">No paired terms match filters.</td></tr>}</tbody>
        </table>
      </div>
      {page?.aggregates.length ? <p className="stt-muted">{page.aggregates.map((item) => `${item.provider_key} ${item.category}: ${pct(item.recall_lift)} lift (${item.paired_clusters} clusters)`).join(" · ")}</p> : null}
      <div className="stt-pagination">
        <span>{page ? `${offset + 1}–${Math.min(offset + limit, page.total)} of ${page.total}` : "Loading..."}</span>
        <button type="button" className="stt-btn stt-btn--secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
        <button type="button" className="stt-btn stt-btn--secondary" disabled={!page || offset + limit >= page.total} onClick={() => setOffset(offset + limit)}>Next</button>
      </div>
    </section>
  );
}

function JudgeHealthPanel({
  health,
  evaluator,
  evaluation,
}: {
  health: BenchmarkRun["summary"]["judge_health"];
  evaluator: BenchmarkRun["summary"]["evaluator"];
  evaluation: BenchmarkJudgeEvaluationContext | null;
}) {
  const blockingReasons = health?.blocking_reasons ?? [];
  const providerHealth = Object.entries(health?.provider_health ?? {});
  const officialEligible = health?.official_metrics_eligible;
  return (
    <section className="stt-evaluator" aria-labelledby="judge-health-title">
      <div className="stt-evaluator-head">
        <div>
      <h4 id="judge-health-title">LLM-as-judge analytics · Judge health</h4>
          <p>Luna reviews each eligible speech cell. This panel reports whether those decisions are reliable enough for provider comparison.</p>
        </div>
      </div>
      <div className="stt-evaluator-strip stt-judge-health-strip">
        <span><small>Contract</small><strong>{evaluation?.policy_version ?? "Historical"}</strong></span>
        <span><small>Model</small><strong>{evaluation?.model ?? "—"}</strong></span>
        <span><small>Schema</small><strong>{evaluation?.schema_version ?? "—"}</strong></span>
        <span><small>LLM coverage</small><strong>{pct(health?.llm_coverage)}</strong></span>
        <span><small>Decisive decision rate</small><strong>{pct(health?.decisive_decision_rate ?? health?.decision_coverage ?? null)}</strong></span>
        <span><small>LLM availability</small><strong>{health ? pct(1 - (health.unavailable / Math.max(1, health.llm_cells))) : "—"}</strong></span>
        <span><small>Proof health</small><strong>{pct(health?.proof_verification ?? null)}</strong></span>
        <span><small>Judge gate</small><strong>{officialEligible == null ? "—" : officialEligible ? "Pass" : "Blocked"}</strong></span>
      </div>
      {blockingReasons.length ? (
        <p className="stt-judge-evaluation-warning" role="status">
          <Info size={13} /> Judge gate blocked: {blockingReasons.map(judgeGateLabel).join(" · ")}
        </p>
      ) : null}
      {providerHealth.length ? (
        <div className="stt-table-wrap">
          <table className="stt-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th className="num">Deterministic</th>
                <th className="num">Luna attempts</th>
                <th className="num">Luna available</th>
                  <th className="num">Route coverage</th>
                <th className="num">Availability</th>
                <th className="num">Proof health</th>
                <th className="num">Proof rejected</th>
                <th>Route gate</th>
              </tr>
            </thead>
            <tbody>
              {providerHealth.map(([provider, item]) => (
                <tr key={provider}>
                  <td className="stt-prov">{provider}</td>
                  <td className="num">{item.deterministic_count}</td>
                  <td className="num">{item.llm_attempts}</td>
                  <td className="num">{item.llm_available}</td>
                  <td className="num">{pct(item.route_coverage ?? item.decision_coverage ?? null)}</td>
                  <td className="num">{pct(item.luna_availability)}</td>
                  <td className="num">{pct(item.proof_verification ?? null)}</td>
                  <td className="num">{item.proof_rejections}</td>
                  <td>
                    <span className={`stt-run-status ${item.coverage_sufficient && !item.gate_reasons.length ? "stt-run-status--completed" : "stt-run-status--failed"}`}>
                      {item.gate_reasons.length ? item.gate_reasons.join(", ") : "pass"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="stt-diagnostic-note">
          Per-provider LLM route analytics were not recorded in this evaluation payload. Re-evaluate with {evaluation?.policy_version ?? "active judge contract"} to expose deterministic coverage, Luna availability, and proof failures.
        </div>
      )}
      <details className="stt-judge-health-details">
        <summary>Show judge diagnostics</summary>
        <div className="stt-evaluator-strip stt-judge-health-strip">
          <span><small>Deterministic coverage</small><strong>{pct(health?.deterministic_coverage)}</strong></span>
          <span><small>Luna coverage</small><strong>{pct(health?.llm_coverage)}</strong></span>
          <span><small>True audio unclear</small><strong>{pct(health?.true_audio_unclear_rate)}</strong></span>
          <span><small>Unavailable</small><strong>{pct(health?.unavailable_rate)}</strong></span>
          <span><small>Proof verified</small><strong>{health ? `${health.proof_verified ?? 0} / ${health.eligible_cells ?? 0}` : "—"}</strong></span>
          <span><small>Proof repaired</small><strong>{health?.proof_repaired ?? 0}</strong></span>
          <span><small>Proof rejected</small><strong>{health?.proof_rejected ?? 0}</strong></span>
          <span><small>Decisive decision rate</small><strong>{pct(health?.decisive_decision_rate ?? health?.decision_coverage ?? null)}</strong></span>
          <span><small>Context-sensitive</small><strong>{health?.context_sensitive ?? "—"}</strong></span>
          <span><small>Low confidence</small><strong>{health?.low_confidence ?? "—"}</strong></span>
          <span><small>Repeated agreement</small><strong>{pct(health?.repeated_agreement ?? null)}</strong></span>
          <span><small>Counterfactual agreement</small><strong>{pct(health?.counterfactual_agreement)}</strong></span>
          <span><small>Keyterm invariance</small><strong>{pct(health?.keyterm_invariance)}</strong></span>
          <span><small>Human audit coverage</small><strong>{pct(evaluator?.audit_coverage)}</strong></span>
        </div>
      </details>
    </section>
  );
}

function JudgeOnlyComparison({
  providers,
  track,
  blocked,
}: {
  providers: [string, BenchmarkProviderSummary][];
  track: BenchmarkRun["summary"]["judge_only"];
  blocked: boolean;
}) {
  const diagnosticOnly = track?.diagnostic_only !== false;
  return (
    <section className="stt-analytics-panel" aria-label="Judge-only comparison" aria-labelledby="judge-only-title">
      <div className="stt-evaluator-head">
        <div>
          <h4 id="judge-only-title">LLM-judge provider comparison {diagnosticOnly ? <span className="stt-badge stt-badge--noisy">Historical diagnostic</span> : <span className="stt-badge stt-badge--ghost">Official judge track</span>}</h4>
          <p>Luna reviews every eligible speech cell. Human reviews remain optional diagnostics and never determine this track.</p>
        </div>
      </div>
      {!track ? (
        <div className="stt-diagnostic-note">
          Historical judge material rates are shown where available. Rebuild analytics to calculate unresolved outcomes, confidence intervals, coverage, ordering, and paired effects.
        </div>
      ) : null}
      {blocked ? (
        <div className="stt-diagnostic-note">Provider ordering hidden. Resolve judge health gates before comparing providers.</div>
      ) : <div className="stt-table-wrap">
        <table className="stt-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Provider</th>
              <th className="num">Equivalent</th>
              <th className="num">Material</th>
              <th className="num">Critical</th>
              <th className="num">Unclear</th>
              <th className="num">Unavailable</th>
              <th className="num">Decisive coverage</th>
              <th className="num">Bad / unresolved</th>
            </tr>
          </thead>
          <tbody>
            {providers
              .map(([provider, summary]) => {
                const judgeOnly = judgeOnlyProviderView(summary);
                const order = track?.ordering.find((item) => item.provider === provider)?.rank;
                return { provider, judgeOnly, order };
              })
              .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER))
              .map(({ provider, judgeOnly, order }) => (
                <tr key={provider}>
                  <td className="num">{order ?? "—"}</td>
                  <td className="stt-prov">{provider}</td>
                  <td className="num">{judgeOnly.summary?.equivalent ?? "—"}</td>
                  <td className="num">{judgeOnly.materialErrors ?? "—"}</td>
                  <td className="num">{judgeOnly.summary?.critical_errors ?? "—"}</td>
                  <td className="num">{judgeOnly.summary?.unclear ?? "—"}</td>
                  <td className="num">{judgeOnly.summary?.unavailable ?? "—"}</td>
                  <td className="num"><PercentWithCi value={judgeOnly.summary?.decisive_coverage ?? null} ci={judgeOnly.summary?.decisive_coverage_ci95 ?? null} /></td>
                  <td className="num">
                    <PercentWithCi value={judgeOnly.badOrUnresolvedRate} ci={judgeOnly.summary?.bad_or_unresolved_rate_ci95 ?? null} />
                    {judgeOnly.legacyFallback ? <small className="stt-ci">material-rate fallback</small> : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>}
      {track?.pairwise.length ? (
        <div className="stt-table-wrap">
          <table className="stt-table">
            <thead><tr><th>Deepgram-relative effect</th><th>Metric</th><th className="num">Paired cells</th><th className="num">Clusters</th><th className="num">Candidate delta</th><th className="num">95% CI</th><th>Status</th></tr></thead>
            <tbody>
              {track.pairwise.flatMap((comparison) => ([
                ["Bad / unresolved", comparison.bad_or_unresolved] as const,
                ["Decisive material error", comparison.material_error] as const,
              ]).map(([label, metric]) => (
                <tr key={`${comparison.candidate}-${label}`}>
                  <td className="stt-prov">{comparison.candidate} vs {comparison.baseline}</td>
                  <td>{label}</td>
                  <td className="num">{metric.paired_cells}</td>
                  <td className="num">{metric.session_clusters}</td>
                  <td className="num">{pp(metric.rate_delta)}</td>
                  <td className="num">{metric.ci95 ? `${pp(metric.ci95[0])} to ${pp(metric.ci95[1])}` : "—"}</td>
                  <td><span className={`stt-run-status stt-run-status--${metric.status === "better" ? "completed" : metric.status === "worse" ? "failed" : "interrupted"}`}>{metric.status}</span></td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function ErrorAnalytics({
  providers,
  scoreTrack,
  onScoreTrackChange,
  onDrillDown,
}: {
  providers: [string, BenchmarkProviderSummary][];
  scoreTrack: ScoreTrack;
  onScoreTrackChange: (value: ScoreTrack) => void;
  onDrillDown: (provider: string, errorType: string) => void;
}) {
  const judgeOnly = scoreTrack === "judge_only";
  return (
    <section className="stt-analytics-panel" aria-labelledby="error-analytics-title">
      <div className="stt-evaluator-head">
        <div>
          <h4 id="error-analytics-title">Error analytics</h4>
          <p>{judgeOnly ? "LLM-judge metrics use selected immutable Luna evaluation. Human audits do not alter this track." : "Human-audit diagnostics are supplemental and never required for LLM-judge scoring."}</p>
        </div>
        <ScoreTrackControl value={scoreTrack} onChange={onScoreTrackChange} />
      </div>
      <div className="stt-table-wrap">
        <table className="stt-table stt-error-analytics-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th className="num"><MetricLabel label={judgeOnly ? "Bad / unresolved" : "Audit bad outcome"} help={judgeOnly ? "Material, unclear, unavailable, provider-failure, and empty-transcript outcomes across all eligible speech cells." : "Supplemental human-audit diagnostic; not used for LLM-judge provider scoring."} /></th>
              <th className="num"><MetricLabel label="Material error" help={judgeOnly ? "LLM-judge material errors among decisive judge outcomes." : "Supplemental human-audit material error diagnostic."} /></th>
              <th className="num"><MetricLabel label="Critical error" help={judgeOnly ? "Selected-judge critical errors among decisive outcomes." : "Weighted rate of human-confirmed critical semantic errors."} /></th>
              <th className="num"><MetricLabel label={judgeOnly ? "Decisive coverage" : "Reviewed"} help={judgeOnly ? "Equivalent or material judge outcomes divided by all eligible speech cells." : "Human-reviewed provider rows divided by speech cells."} /></th>
              <th className="num"><MetricLabel label="Unclear" help={judgeOnly ? "LLM-judge rows requiring audio distinction. They are outside decisive semantic-error denominators." : "Supplemental human-audit ambiguity diagnostic."} /></th>
              <th className="num"><MetricLabel label="Reference advisory" help="Human-confirmed suspect-reference count. This diagnostic does not replace Luna's row decision." /></th>
              <th><MetricLabel label="Classified causes" help="Judge error labels can co-occur, so these counts are labels rather than mutually exclusive rows. Select a cause to inspect matching provider rows." /></th>
            </tr>
          </thead>
          <tbody>
            {providers.map(([provider, summary]) => {
              const judge = judgeOnlyProviderView(summary);
              // Summaries before v9 carry no reason, so they keep the historical
              // fallback. From v9 the arena states why it withheld the weighted
              // rate, and printing a different metric under this heading would
              // undo exactly what the withholding was for. Both the estimate and
              // its interval are suppressed together: an interval without a
              // point estimate reads as a measurement, and the CI fields are
              // typed independently of the reason, so this cannot lean on the
              // backend nulling them in the same breath.
              const withheldWeightedRate = summary.weighted_rate_unavailable_reason
                ? WEIGHTED_RATE_UNAVAILABLE_LABELS[summary.weighted_rate_unavailable_reason]
                  ?? "not computed"
                : null;
              const humanMaterialRate = summary.conditional_semantic_error_rate
                ?? (summary.human_scored ? summary.human_material_errors / summary.human_scored : null);
              const causes = Object.entries(summary.error_types ?? {})
                .filter(([type, count]) => REVIEW_ERROR_TYPES.includes(type) && count > 0)
                .sort(([, left], [, right]) => right - left);
              return (
                <tr key={provider}>
                  <td className="stt-prov">{provider}</td>
                  <td className="num">
                    <PercentWithCi
                      value={judgeOnly
                        ? judge.badOrUnresolvedRate
                        : withheldWeightedRate
                          ? null
                          : summary.unconditional_bad_outcome_rate ?? summary.audit_adjusted_material_error_rate}
                      ci={judgeOnly
                        ? judge.summary?.bad_or_unresolved_rate_ci95 ?? null
                        : withheldWeightedRate
                          ? null
                          : summary.unconditional_bad_outcome_ci95 ?? null}
                    />
                    {judgeOnly && judge.legacyFallback ? <small className="stt-ci">legacy material-rate fallback</small> : null}
                    {!judgeOnly && withheldWeightedRate ? <small className="stt-ci">{withheldWeightedRate}</small> : null}
                  </td>
                  <td className="num">
                    <PercentWithCi
                      value={judgeOnly ? judge.materialErrorRate : humanMaterialRate}
                      ci={judgeOnly ? judge.summary?.material_error_rate_ci95 ?? null : summary.conditional_semantic_ci95 ?? null}
                    />
                    <small className="stt-ci">{judgeOnly ? `${judge.materialErrors ?? "—"} / ${judge.decisiveCells ?? "—"}` : `${summary.human_material_errors} / ${summary.human_scored}`}</small>
                  </td>
                  <td className="num">
                    <PercentWithCi
                      value={judgeOnly
                        ? judge.summary?.critical_error_rate ?? null
                        : withheldWeightedRate
                          ? null
                          : summary.critical_error_rate ?? null}
                      ci={judgeOnly
                        ? judge.summary?.critical_error_rate_ci95 ?? null
                        : withheldWeightedRate
                          ? null
                          : summary.critical_error_ci95 ?? null}
                    />
                    {!judgeOnly && withheldWeightedRate ? <small className="stt-ci">{withheldWeightedRate}</small> : null}
                  </td>
                  <td className="num">
                    {judgeOnly ? (
                      <PercentWithCi value={judge.summary?.decisive_coverage ?? null} ci={judge.summary?.decisive_coverage_ci95 ?? null} />
                    ) : (
                      <>{summary.human_reviewed}<small className="stt-ci">of {summary.speech_cells} speech cells</small></>
                    )}
                  </td>
                  <td className="num">{judgeOnly ? (judge.summary?.unclear ?? "—") : (summary.unclear_cells ?? "—")}</td>
                  <td className="num">{summary.reference_issue_excluded ?? "—"}<small className="stt-ci">of {summary.reference_issue_cells ?? 0} flagged</small></td>
                  <td>
                    <div className="stt-error-cause-list">
                      {causes.length ? causes.map(([type, count]) => (
                        <button
                          key={type}
                          type="button"
                          title={`Show ${provider} rows classified as ${type.replaceAll("_", " ")}`}
                          onClick={() => onDrillDown(provider, type)}
                        >
                          <span>{type.replaceAll("_", " ")}</span>
                          <strong>{count}</strong>
                        </button>
                      )) : <span className="stt-muted">No classified semantic errors</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BenchmarkResultRow({
  runId,
  row,
  blindAudit,
  onReviewed,
}: {
  runId: string;
  row: BenchmarkRow;
  blindAudit: boolean;
  onReviewed: () => Promise<void>;
}) {
  const [notes, setNotes] = useState(row.review?.notes ?? "");
  const [severity, setSeverity] = useState<"none" | "non_critical" | "critical" | "unknown">(row.review?.severity ?? "non_critical");
  const [errorType, setErrorType] = useState(row.review?.error_types?.[0] ?? "other");
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [savedReview, setSavedReview] = useState<BenchmarkReview | null>(row.review);
  const score = row.judge?.decision === "equivalent" ? 1 : row.judge?.decision === "material_error" ? 0 : null;
  const keytermAttachment = benchmarkKeytermAttachmentView(
    row.sample.critical_terms ?? [],
    row.keyterm_mode,
    row.keyterm_source,
  );
  const displayedKeytermMode = row.hint_variant ?? row.keyterm_mode;
  const baselineVariant = isCellReviewBaseline(displayedKeytermMode);
  const sttPrompt = row.sample.stt_hint_bundle?.prompt?.trim() || null;
  const hintVariant = row.hint_variant ?? row.hint_delivery?.variant ?? "baseline";
  const promptRequested = hintVariant === "prompt" || hintVariant === "combined";
  const promptDelivered = row.hint_delivery?.delivered_fields?.includes("prompt") ?? false;
  const promptDropped = row.hint_delivery?.dropped_fields?.includes("prompt") ?? false;
  const promptStatus = !sttPrompt
    ? "No generated prompt"
    : !promptRequested
      ? `Withheld · ${hintVariant}`
      : promptDelivered
        ? "Sent to provider"
        : promptDropped
          ? "Provider did not accept"
          : "Requested for provider";
  const noEligiblePromptTerms = row.sample.keyterm_extraction?.validation_outcome === "no_eligible_terms";
  const providerKeywordAdaptations = row.hint_delivery?.adapted_keywords
    ?? row.keyterm_delivery?.adapted_terms
    ?? [];
  const activeVerdict = savedReview?.human_verdict ?? row.review?.human_verdict;
  const review = async (verdict: "equivalent" | "material_error" | "unclear") => {
    setSaving(true);
    setLocalError(null);
    let reviewSaved = false;
    try {
      const saved = await arenaApi.reviewBenchmarkRow(runId, row.cell_id, verdict, notes || null, {
        severity: verdict === "equivalent" ? "none" : verdict === "unclear" ? "unknown" : severity,
        errorTypes: verdict === "material_error" ? [errorType] : [],
        referenceIssueSuspected: false,
        referenceQuality: "unknown",
      });
      setSavedReview(saved);
      reviewSaved = true;
      toast.success(`Review saved as ${verdict.replaceAll("_", " ")}`, {
        description: saved.needs_adjudication
          ? "Another review or adjudication is still required."
          : "Refreshing benchmark analytics.",
      });
      await onReviewed();
    } catch (cause) {
      setLocalError(reviewSaved
        ? `Review was saved, but analytics refresh failed: ${formatError(cause)}`
        : formatError(cause));
    } finally {
      setSaving(false);
    }
  };
  const adjudicate = async (verdict: "equivalent" | "material_error" | "unclear") => {
    setSaving(true);
    setLocalError(null);
    let adjudicationSaved = false;
    try {
      const saved = await arenaApi.adjudicateBenchmarkRow(runId, row.cell_id, verdict, notes || null, {
        severity: verdict === "equivalent" ? "none" : verdict === "unclear" ? "unknown" : severity,
        errorTypes: verdict === "material_error" ? [errorType] : [],
        referenceQuality: "unknown",
      });
      setSavedReview(saved);
      adjudicationSaved = true;
      toast.success(`Adjudication saved as ${verdict.replaceAll("_", " ")}`);
      await onReviewed();
    } catch (cause) {
      setLocalError(adjudicationSaved
        ? `Adjudication was saved, but analytics refresh failed: ${formatError(cause)}`
        : formatError(cause));
    } finally {
      setSaving(false);
    }
  };
  const policyLabel = (row.judge?.policy_version ?? row.judge?.prompt_version ?? "unknown")
    .replace("semantic-preservation-", "")
    .toUpperCase();
  const judgeAttribution = row.judge?.scorer === "deterministic"
    ? `Deterministic rule · policy ${policyLabel}`
    : row.judge?.scorer === "judge_guard"
      ? `Judge guard · policy ${policyLabel}`
      : row.judge?.scorer === "none"
        ? `Unscored · policy ${policyLabel}`
        : `${row.judge?.provider === "azure_openai" ? "Azure" : row.judge?.provider ?? "LLM"} · ${row.judge?.model ?? "unavailable"} · policy ${policyLabel}`;

  return (
    <article className="stt-benchmark-row">
      <header>
        <div>
          <strong>{blindAudit ? row.audit_label ?? "Blind candidate" : row.provider_key}</strong>
          {!blindAudit ? <span className="stt-badge stt-badge--ghost">{cellReviewVariantLabel(displayedKeytermMode)}</span> : null}
          {!blindAudit && row.audit_scope === "diagnostic" ? (
            <span className={`stt-badge ${baselineVariant ? "stt-badge--ghost" : "stt-badge--noisy"}`}>
              {cellReviewTrackLabel(row.keyterm_source ?? "reviewed", displayedKeytermMode)}
            </span>
          ) : null}
          {!blindAudit ? <span className={`stt-decision stt-decision--${row.judge?.decision ?? "unavailable"}`}>
            {row.judge?.decision === "unclear" ? "Unclear · audio review required" : `score ${score ?? "—"} · ${(row.judge?.decision ?? "pending").replaceAll("_", " ")}`}
          </span> : <span className="stt-badge stt-badge--noisy">blind audit</span>}
          {!blindAudit && row.judge?.audit_required && !row.review ? (
            <span className="stt-badge stt-badge--noisy">optional audit</span>
          ) : null}
        </div>
        <AudioPlayer uri={row.sample.audio_uri} compact />
      </header>

      <div className="stt-benchmark-transcripts">
        <div>
          <span>Approved label · v{row.sample.label_version}</span>
          <p>{row.sample.reference_text || "No speech"}</p>
        </div>
        <div>
          <span>Provider transcript</span>
          <p className={row.error ? "stt-bad" : ""}>{row.error ? `Error: ${row.error}` : row.hypothesis || "(empty)"}</p>
        </div>
      </div>

      {!blindAudit ? (
        <details className="stt-cell-diagnostics">
          <summary>Transcript diagnostics <span>keyterms, OpenAI prompt, provider metadata, delivery details</span></summary>
        <section
          className={`stt-benchmark-keyterms stt-benchmark-keyterms--${keytermAttachment.state}`}
          aria-label="Keyterm prompt attachment"
        >
          <div className="stt-benchmark-keyterm-title">
            <Tags size={14} aria-hidden="true" />
            <MetricLabel
              label="Keyterm prompt"
              help="Accepted terms frozen with this audio sample. Prompted cells send them to the STT provider; baseline cells retain them for comparison but do not send them."
            />
          </div>
          {keytermAttachment.terms.length ? (
            <ul
              className="stt-benchmark-keyterm-values"
              aria-label="Attached keyterm values"
            >
              {keytermAttachment.terms.map((term) => {
                const observation = row.keyterm_matches?.find(
                  (item) => item.term === term,
                );
                const observationStatus = observation?.observation_status
                  ?? (observation?.present === true
                    ? "observed_in_transcript"
                    : observation?.present === false
                      ? "not_observed_in_transcript"
                      : null);
                const observed = observationStatus === "observed_in_transcript";
                return (
                  <li key={term} className={observationStatus ? (observed ? "matched" : "missing") : "unknown"}>
                    {term}<small>{observationStatus ? (observed ? "observed in transcript" : "not observed in transcript") : "observation unavailable"}</small>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="stt-benchmark-keyterm-empty">
              {noEligiblePromptTerms ? "No eligible prompt terms" : "No values frozen with this sample"}
            </span>
          )}
          <div className="stt-benchmark-keyterm-meta">
            <strong>{keytermAttachment.statusLabel}</strong>
            <small>
              {keytermAttachment.sourceLabel} · {keytermAttachment.termCountLabel}
            </small>
            {row.keyterm_hash ? <small>Hash {row.keyterm_hash.slice(0, 12)}</small> : null}
            {row.hint_delivery?.requested_keyword_count != null ? (
              <small>
                Provider keywords {row.hint_delivery.delivered_keyword_count ?? 0}/{row.hint_delivery.requested_keyword_count}
              </small>
            ) : null}
            {row.hint_delivery?.dropped_keywords?.length ? (
              <small>Provider dropped: {row.hint_delivery.dropped_keywords.join(", ")}</small>
            ) : null}
            {providerKeywordAdaptations.length ? (
              <details className="stt-keyterm-prompt">
                <summary>Provider keyword adaptations</summary>
                {providerKeywordAdaptations.map((adaptation, index) => (
                  <small key={`${adaptation.source_term}-${index}`}>
                    {adaptation.source_term} → {adaptation.delivered_terms.join(" + ") || "not delivered"} · {adaptation.action.replaceAll("_", " ")}
                  </small>
                ))}
              </details>
            ) : null}
            {row.keyterm_delivery?.provider_request_id || row.stt_metadata?.request_id ? <small>Provider request {row.keyterm_delivery?.provider_request_id ?? row.stt_metadata?.request_id}</small> : null}
          </div>
        </section>
        <section className="stt-benchmark-stt-prompt" aria-label="OpenAI STT prompt">
          <div className="stt-benchmark-stt-prompt-header">
            <strong>OpenAI STT prompt</strong>
            <span>{promptStatus}</span>
          </div>
          {sttPrompt ? <p>{sttPrompt}</p> : <small>No generated prompt for this keyterm source.</small>}
          <div className="stt-benchmark-stt-prompt-meta">
            <small>Variant: {hintVariant}</small>
            {row.hint_delivery?.provider_request_id ? <small>Provider request: {row.hint_delivery.provider_request_id}</small> : null}
            {row.hint_delivery?.prompt_hash ? <small>Hash: {row.hint_delivery.prompt_hash.slice(0, 12)}</small> : null}
          </div>
        </section>
          <SttMetadataPanel row={row} />
        </details>
      ) : null}

      <div className="stt-benchmark-metrics">
        <span><MetricLabel label="nWER" help="Normalized word error rate for this sample. Lower means the transcript is closer to the approved label." /> <strong>{pct(row.nwer)}</strong></span>
        <span><MetricLabel label="CER" help="Character error rate for this sample. Lower means fewer character-level transcription differences." /> <strong>{pct(row.cer)}</strong></span>
        <span><MetricLabel label="TTFT" help="Time to first transcript result from the provider." /> <strong>{ms(row.ttft_ms)}</strong></span>
        <span><MetricLabel label="Total" help="Total provider transcription time for this sample." /> <strong>{ms(row.total_ms)}</strong></span>
        <span><MetricLabel label="Finalize" help="Time until the provider finalized the transcript for this sample." /> <strong>{ms(row.finalization_ms)}</strong></span>
      </div>

      {row.judge && !blindAudit ? (
        <div className="stt-judge-reason">
          <span>{judgeAttribution}</span>
          <p>{row.judge.reason}</p>
          {row.judge.luna_decision ? (
            <small>Luna decision: {row.judge.luna_decision.replaceAll("_", " ")}</small>
          ) : null}
          {row.judge.tutor_sufficiency ? (
            <small>Tutor meaning: {row.judge.tutor_sufficiency.replaceAll("_", " ")}</small>
          ) : null}
          {row.judge.decision_confidence ? (
            <small>Judge confidence: {row.judge.decision_confidence}{row.judge.context_sensitive ? " · context-sensitive" : ""}</small>
          ) : null}
          {row.judge.critical_slot_changes?.length ? (
            <small title={row.judge.critical_slot_changes.map((change) => change.downstream_impact).join("; ")}>
              Critical changes: {row.judge.critical_slot_changes.slice(0, 2).map((change) => change.downstream_impact).join("; ")}{row.judge.critical_slot_changes.length > 2 ? ` (+${row.judge.critical_slot_changes.length - 2})` : ""}
            </small>
          ) : null}
          {row.judge.semantic_repeat?.checked ? (
            <small>Semantic repeat: {row.judge.semantic_repeat.agreement ? "agreement" : "disagreement"}</small>
          ) : null}
          {row.judge.decision_source === "luna_proof_rejected" ? (
            <small>Decision promoted from Luna; proof rejection remains a run gate failure.</small>
          ) : null}
          {row.judge.proof_rejection_code ? (
            <small className="stt-bad">
              Proof rejected: {row.judge.proof_rejection_code.replaceAll("_", " ")}
            </small>
          ) : null}
          {row.judge.proof_status === "repaired" ? <small>Proof repaired locally</small> : null}
          {row.judge.error_types.length ? <small>{row.judge.error_types.join(", ")}</small> : null}
          {row.judge.decision === "unclear" ? <small>Reason: {(row.judge.unclear_reason ?? "other").replaceAll("_", " ")} · {row.context_used ? "bounded context used" : "reference only"}</small> : null}
          {row.reference_review_recommended && row.reference_quality !== "suspect" ? <small>Reference review suggested</small> : null}
          {row.reference_quality === "suspect" ? <small className="stt-bad">Excluded from official provider metrics pending adjudication.</small> : null}
        </div>
      ) : null}

      <details className="stt-optional-audit">
        <summary>Optional human audit</summary>
      <div className="stt-review-controls">
        <input
          className="stt-input"
          value={notes}
          maxLength={2000}
          placeholder="Transcript review note (optional)"
          onChange={(event) => setNotes(event.target.value)}
        />
        <select className="stt-select" value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)} aria-label="Review severity">
          <option value="non_critical">Non-critical</option>
          <option value="critical">Critical</option>
          <option value="unknown">Unknown</option>
          <option value="none">No error</option>
        </select>
        <select className="stt-select" value={errorType} onChange={(event) => setErrorType(event.target.value)} aria-label="Review error type">
          {REVIEW_ERROR_TYPES.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
        </select>
        <button type="button" title="Equivalent" aria-pressed={activeVerdict === "equivalent"} className={activeVerdict === "equivalent" ? "active good" : ""} disabled={saving} onClick={() => review("equivalent")}>
          <Check size={14} /> Equivalent
        </button>
        <button type="button" title="Material error" aria-pressed={activeVerdict === "material_error"} className={activeVerdict === "material_error" ? "active bad" : ""} disabled={saving} onClick={() => review("material_error")}>
          <X size={14} /> Material error
        </button>
        <button type="button" title="Unclear" aria-pressed={activeVerdict === "unclear"} className={activeVerdict === "unclear" ? "active" : ""} disabled={saving} onClick={() => review("unclear")}>
          <AlertTriangle size={14} /> Unclear
        </button>
        {row.review_state?.adjudication_required ? <button type="button" title="Adjudicate this audit cell using the latest review verdict" disabled={saving} onClick={() => adjudicate(row.reviews?.at(-1)?.human_verdict ?? "material_error")}>
          <FlaskConical size={14} /> Adjudicate latest
        </button> : null}
      </div>
      </details>
      {savedReview ? (
        <small className="stt-review-saved" role="status" aria-live="polite">
          <Check size={13} />
          Saved as {savedReview.human_verdict.replaceAll("_", " ")}
          {savedReview.needs_adjudication ? " · awaiting another review or adjudication" : ""}
        </small>
      ) : null}
      {localError ? <small className="stt-bad">{localError}</small> : null}
    </article>
  );
}

function SttMetadataPanel({ row }: { row: BenchmarkRow }) {
  const [open, setOpen] = useState(false);
  const metadata = row.stt_metadata;
  const wordConfidence = metadata?.word_confidence;
  const transcriptConfidence = metadata?.transcript_confidence;
  const languageConfidence = metadata?.language?.confidence;
  const responseGaps = open
    ? (row.inter_response_latencies_ms ?? []).filter(Number.isFinite)
    : [];
  const hasGlobalLowestWords = metadata?.lowest_confidence_words != null;
  const lowestConfidenceWords = open
    ? [...(metadata?.lowest_confidence_words ?? metadata?.words ?? [])]
        .filter((word) => word.confidence != null)
        .sort((left, right) => (left.confidence ?? 1) - (right.confidence ?? 1))
        .slice(0, 8)
    : [];
  const lowestConfidenceLabel = !hasGlobalLowestWords && metadata?.words_truncated
    ? `Lowest-confidence words in captured subset (${metadata.words.length} of ${metadata.word_count})`
    : "Lowest-confidence words";
  const confidenceLabel = wordConfidence
    ? `word p10 ${pct(wordConfidence.p10)}`
    : transcriptConfidence
      ? `transcript ${pct(transcriptConfidence.mean)}`
      : "confidence unavailable";

  return (
    <details
      className="stt-provider-metadata"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        <span><Info size={12} aria-hidden="true" /> STT metadata</span>
        <small>{confidenceLabel}</small>
      </summary>
      {open ? (
        <>
          <div className="stt-provider-metadata-grid">
            <span><MetricLabel label="Signal" help="Provider-native confidence field used for this row. Do not compare raw values across providers without calibration." /> <strong>{metadata?.confidence_kind?.replaceAll("_", " ") ?? "—"}</strong></span>
            <span><MetricLabel label="Word mean" help="Mean provider-native confidence across final words. Confidence definitions differ by provider and require provider-specific calibration." /> <strong>{pct(wordConfidence?.mean)}</strong></span>
            <span><MetricLabel label="Word P10" help="10th percentile final-word confidence. This exposes the low-confidence tail for grey-zone analysis." /> <strong>{pct(wordConfidence?.p10)}</strong></span>
            <span><MetricLabel label="Word min" help="Lowest final-word confidence in this transcript." /> <strong>{pct(wordConfidence?.min)}</strong></span>
            <span><MetricLabel label="Transcript" help="Mean provider-native confidence across final transcript segments, when supplied by the provider." /> <strong>{pct(transcriptConfidence?.mean)}</strong></span>
            <span><MetricLabel label="Language" help="Provider language metadata. The source indicates whether it was configured or detected." /> <strong>{metadata?.language ? `${metadata.language.code} · ${metadata.language.source ?? "provider"}${languageConfidence == null ? "" : ` · ${pct(languageConfidence)}`}` : "—"}</strong></span>
            <span><MetricLabel label="Words" help="Number of provider word metadata entries captured for this transcript." /> <strong>{metadata?.word_count ?? "—"}</strong></span>
            <span><MetricLabel label="Events" help="Input audio chunks followed by transcript events emitted by the provider." /> <strong>{row.chunks_in ?? "—"} / {row.chunks_out ?? "—"}</strong></span>
            <span><MetricLabel label="Gap P95" help="95th percentile wall-clock gap between transcript events." /> <strong>{ms(percentile(responseGaps, 0.95))}</strong></span>
            <span><MetricLabel label="Final events" help="Number of final transcript events used to assemble the provider transcript." /> <strong>{metadata?.final_event_count ?? "—"}</strong></span>
          </div>
          {lowestConfidenceWords.length ? (
            <div className="stt-provider-word-confidence">
              <span>{lowestConfidenceLabel}</span>
              <div>
                {lowestConfidenceWords.map((word, index) => (
                  <span key={`${word.text}-${word.start_ms ?? index}`}>
                    <strong>{word.text}</strong>
                    {pct(word.confidence)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {metadata?.request_id || metadata?.session_id ? (
            <div className="stt-provider-identifiers">
              {metadata.request_id ? <span>Request <code>{metadata.request_id}</code></span> : null}
              {metadata.session_id ? <span>Session <code>{metadata.session_id}</code></span> : null}
            </div>
          ) : null}
        </>
      ) : null}
    </details>
  );
}

function SliceHeatmap({
  providers,
  scoreTrack,
  onScoreTrackChange,
}: {
  providers: Array<[string, BenchmarkProviderSummary]>;
  scoreTrack: ScoreTrack;
  onScoreTrackChange: (value: ScoreTrack) => void;
}) {
  const dimensions = [...new Set(providers.flatMap(([, summary]) => Object.keys(summary.slices ?? {})))];
  const [dimension, setDimension] = useState("");
  const activeDimension = dimensions.includes(dimension) ? dimension : dimensions[0];
  if (!activeDimension) return null;
  const values = [...new Set(providers.flatMap(([, summary]) => Object.keys(summary.slices?.[activeDimension] ?? {})))].sort();
  return (
    <section className="stt-analytics-panel" aria-labelledby="slice-heatmap-title">
      <div className="stt-evaluator-head">
        <div><h4 id="slice-heatmap-title">Quality slices</h4><p>{scoreTrack === "judge_only" ? "LLM-judge slices show bad or unresolved outcomes and decisive coverage." : "Human-audit slices are supplemental diagnostics."}</p></div>
        <div className="stt-score-controls">
          <ScoreTrackControl value={scoreTrack} onChange={onScoreTrackChange} />
          <select className="stt-select" value={activeDimension} onChange={(event) => setDimension(event.target.value)}>
            {dimensions.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
          </select>
        </div>
      </div>
      <div className="stt-table-wrap">
        <table className="stt-table stt-slice-table">
          <thead><tr><th>Provider</th>{values.map((value) => <th key={value} className="num">{value.replaceAll("_", " ")}</th>)}</tr></thead>
          <tbody>{providers.map(([provider, summary]) => <tr key={provider}><td className="stt-prov">{provider}</td>{values.map((value) => {
            const slice = summary.slices?.[activeDimension]?.[value];
            if (!slice) return <td key={value} className="num">—</td>;
            const judgeOnlyCounts = judgeOnlySliceCounts(
              slice.judge_only_decisive_cells,
              slice.judge_only_eligible_cells,
            );
            return (
              <td key={value} className="num">
                <strong>{pct(scoreTrack === "judge_only" ? slice.judge_only_bad_or_unresolved_rate : slice.material_error_rate)}</strong>
                <small className="stt-ci">
                  {scoreTrack === "judge_only"
                    ? judgeOnlyCounts
                      ? `${judgeOnlyCounts.decisiveCells} decisive / ${judgeOnlyCounts.eligibleCells} eligible · ${pct(slice.judge_only_decisive_coverage)} coverage`
                      : "counts unavailable"
                    : `n=${slice.semantic_scored}/${slice.cells}`}
                </small>
              </td>
            );
          })}</tr>)}</tbody>
        </table>
      </div>
    </section>
  );
}
